import {
  Checkbox,
  Menu,
  notify,
  observeResize,
  useConfirmModal,
} from '@affine/component';
import { usePlanGate } from '@affine/core/components/affine/feature-gate/plan-gated-surface';
import { useCanOpenWorkspacePlans } from '@affine/core/components/affine/feature-gate/use-can-open-workspace-plans';
import { CommentPanel } from '@affine/core/components/comment/sidebar';
import { useWorkspaceInfo } from '@affine/core/components/hooks/use-workspace-info';
import {
  AuthService,
  type AvatarAssetCacheInput,
  AvatarAssetCacheService,
  ServerService,
  toAbsoluteAvatarAssetUrl,
  WorkspaceServerService,
} from '@affine/core/modules/cloud';
import type { DocCommentEntity } from '@affine/core/modules/comment/entities/doc-comment';
import { CommentPanelService } from '@affine/core/modules/comment/services/comment-panel-service';
import { DocCommentManagerService } from '@affine/core/modules/comment/services/doc-comment-manager';
import type {
  CommentTargetAnchor,
  DocComment,
} from '@affine/core/modules/comment/types';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import type {
  PDF,
  PDFMeta,
  PDFPageTextContent,
  PDFRendererState,
} from '@affine/core/modules/pdf';
import { PDFService, PDFStatus } from '@affine/core/modules/pdf';
import {
  Item,
  List,
  ListEndPadding,
  ListPadding,
  ListWithSmallGap,
  LoadingSvg,
  PDFPageRenderer,
  type PDFVirtuosoContext,
  type PDFVirtuosoProps,
  Scroller,
  ScrollSeekPlaceholder,
} from '@affine/core/modules/pdf/views';
import {
  readPdfPageBitmapCache,
  writePdfPageBitmapCache,
} from '@affine/core/modules/pdf/views/page-bitmap-cache';
import {
  PDF_PAGE_COMMENT_COLOR,
  PDF_PAGE_HIGHLIGHT_COLORS,
  PDF_PAGE_SELECTION_COLOR,
  type PDFPageHighlightRect,
} from '@affine/core/modules/pdf/views/pdf-highlight-overlay';
import {
  PDF_DIGITAL_SIGNATURE_FEATURE_KEYS,
  WORKSPACE_MAIL_FEATURE_KEYS,
} from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { MailComposeHandoffService } from '@affine/core/modules/streamlain/mail/mail-compose-handoff';
import { uploadWorkspaceBlob } from '@affine/core/modules/streamlain/workflow-viewer/widgets/upload-blob';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { resolveBlobDownloadName } from '@affine/core/utils/blob-display-name';
import { Permission } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import track from '@affine/track';
import {
  ArrowDownSmallIcon,
  CloseIcon,
  CommentIcon,
  DeleteIcon,
  HighLightLinearIcon,
  ImageIcon,
  InformationIcon,
  ReauthenticationIcon,
  TocIcon,
} from '@blocksuite/icons/rc';
import { cssVarV2 } from '@streamlain/theme/v2';
import { downloadBlob } from '@streamlain/workspace-editor/shared/resource';
import { LiveData, useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import { nanoid } from 'nanoid';
import {
  type ChangeEvent,
  type ClipboardEvent as ReactClipboardEvent,
  type CSSProperties,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';

import { resolveDigitalSigningStep } from '../../../desktop/dialogs/setting/workspace-setting/signatures/signing-readiness';
import {
  getPdfSignatureErrorMessage,
  notifyPdfSignatureError,
  type PdfSigningJobInfo,
  type SignedPdfVersionInfo,
  useSignedPdfVersionForBlob,
  useWorkspacePdfSignatures,
} from '../../../desktop/dialogs/setting/workspace-setting/signatures/use-workspace-pdf-signatures';
import type {
  AttachmentViewerBaseProps,
  AttachmentViewerSource,
} from '../types';
import { getAttachmentBlob } from '../utils';
import { PdfDigitalSignaturePanel } from './pdf-digital-signature-panel';
import {
  formatPdfFindCount,
  formatPdfFindNoticeLabel,
  getActivePdfFindHit,
  INITIAL_PDF_FIND_STATE,
  pdfFindReducer,
  type PDFFindState,
  type PDFPageSearchHit,
} from './pdf-find-controller';
import { runHybridPdfFindSource } from './pdf-find-source';
import { buildAnnotatedPdfBytes, printPdfFrames } from './pdf-frame-export';
import { createPdfSourceIndexReviewLocator } from './pdf-index-review';
import { PdfDocumentTextProvider } from './pdf-local-find-provider';
import {
  createPDFViewerNavigationKey,
  normalizePDFPageIndex,
  type PDFViewerNavigation,
} from './pdf-navigation';
import {
  PdfSearchCoverageBadge,
  PdfSearchCoverageCoachmark,
} from './pdf-search-coverage-badge';
import {
  createNavigationPdfSearchHit,
  getPageHighlightRects,
} from './pdf-search-hit-mapping';
import {
  getPdfSignatureFileNameWithoutExtension,
  PdfSignatureCreator,
  type PdfSignatureCreatorResult,
} from './pdf-signature-creator';
import {
  readPdfSignaturePlacementClipboard,
  writePdfSignaturePlacementClipboard,
} from './pdf-signature-placement-clipboard';
import {
  createPdfSignaturePlacement,
  pastePdfSignaturePlacement,
  PDF_SIGNATURE_DEFAULT_RECT,
  removePdfSignaturePlacement,
  updatePdfSignaturePlacement,
  upsertPdfSignaturePlacement,
  useStoredPdfSignaturePlacements,
} from './pdf-signature-placements';
import { PdfSignedBadge } from './pdf-signed-badge';
import {
  fetchSignedPdfVersionFile,
  focusSignedPdfCopyAttachment,
  insertSignedPdfCopyBesideSource,
} from './pdf-signed-copy-placement';
import { getSignedPdfCopyPlacedLabel } from './pdf-signed-version-labels';
import { formatSignedPdfVersionName } from './pdf-signing-status-labels';
import {
  getPdfCommentHighlightRects,
  persistPdfTextHighlight,
  removePdfTextHighlight,
  removePdfTextHighlightForComment,
  useStoredPdfTextHighlights,
} from './pdf-text-highlights';
import { usePdfViewContext } from './pdf-view-context';
import {
  createPdfFullscreenStorageKey,
  readPdfFullscreenViewportState,
  writePdfFullscreenViewportState,
} from './pdf-viewer-state';
import {
  buildPdfWithVisualSignatures,
  canInsertVisualSignatures,
} from './pdf-visual-signature-export';
import { PdfVisualSignatureOverlay } from './pdf-visual-signature-overlay';
import * as styles from './styles.css';
import { usePdfSearchCoverage } from './use-pdf-search-coverage';
import { fitToPage } from './utils';

const PDF_THUMBNAIL_PANEL_MIN_WIDTH = 120;
const PDF_THUMBNAIL_PANEL_MAX_WIDTH = 320;
const PDF_THUMBNAIL_OVERLAY_BREAKPOINT = 760;
const PDF_FIND_LIMIT = 100;
const PDF_ZOOM_MIN = 0.65;
const PDF_ZOOM_MAX = 2.5;
const PDF_PRINT_EVENT = 'streamlain:pdf-print-request';
const PDF_DOWNLOAD_EVENT = 'streamlain:pdf-download-request';
const PDF_SEND_MAIL_EVENT = 'streamlain:pdf-send-mail-request';
const PDF_MAIL_STATE_EVENT = 'streamlain:pdf-mail-state-changed';
const PDF_COMMENTS_TOGGLE_EVENT = 'streamlain:pdf-comments-toggle';
const PDF_COMMENTS_STATE_EVENT = 'streamlain:pdf-comments-state-changed';
const PDF_SIGNATURE_STATE_EVENT = 'streamlain:pdf-signature-state-changed';
const PDF_SIGNATURE_TOGGLE_EVENT = 'streamlain:pdf-signature-toggle';

type PdfSignaturePanelMode = 'digital' | 'visual';

function formatSignatureTimestamp(date = new Date()) {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )} ${pad(date.getHours())}-${pad(date.getMinutes())}`;
}

function getNavigationFallbackFindHit(
  state: PDFFindState,
  query: string
): PDFPageSearchHit | undefined {
  const activeHit = getActivePdfFindHit(state);
  if (activeHit?.source !== 'navigation') return undefined;
  if (activeHit.snippet.trim() !== query) return undefined;
  return activeHit;
}

const PDF_HIGHLIGHT_COLORS = [
  {
    id: 'red',
    value: PDF_PAGE_HIGHLIGHT_COLORS.red,
  },
  {
    id: 'orange',
    value: PDF_PAGE_HIGHLIGHT_COLORS.orange,
  },
  {
    id: 'yellow',
    value: PDF_PAGE_HIGHLIGHT_COLORS.yellow,
  },
  {
    id: 'green',
    value: PDF_PAGE_HIGHLIGHT_COLORS.green,
  },
  {
    id: 'teal',
    value: PDF_PAGE_HIGHLIGHT_COLORS.teal,
  },
  {
    id: 'blue',
    value: PDF_PAGE_HIGHLIGHT_COLORS.blue,
  },
  {
    id: 'purple',
    value: PDF_PAGE_HIGHLIGHT_COLORS.purple,
  },
  {
    id: 'grey',
    value: PDF_PAGE_HIGHLIGHT_COLORS.grey,
  },
] as const;
const DEFAULT_PDF_HIGHLIGHT_COLOR = PDF_HIGHLIGHT_COLORS[2].value;
const PDF_SELECTION_PREVIEW_COLOR = PDF_PAGE_SELECTION_COLOR;
const PDF_COMMENT_HIGHLIGHT_COLOR = PDF_PAGE_COMMENT_COLOR;
const PDF_PAGE_LAYER_SELECTOR = '[data-pdf-page-index]';
const PDF_PAGE_FRAME_SELECTOR = '[data-pdf-page-frame-index]';
const PDF_GLYPH_SELECTOR = '[data-pdf-text-glyph="true"]';
const PDF_EAGER_PAGE_CACHE_LIMIT = 12;
const PDF_PAGE_CACHE_PREFETCH_BEFORE = 1;
const PDF_PAGE_CACHE_PREFETCH_AFTER = 2;

function getPdfPageCachePrefetchCandidates(pageCount: number, cursor: number) {
  if (pageCount <= 0) return [];
  if (pageCount <= PDF_EAGER_PAGE_CACHE_LIMIT) {
    return Array.from({ length: pageCount }, (_, index) => index);
  }

  const candidates = new Set<number>();
  for (
    let pageNum = cursor - PDF_PAGE_CACHE_PREFETCH_BEFORE;
    pageNum <= cursor + PDF_PAGE_CACHE_PREFETCH_AFTER;
    pageNum++
  ) {
    if (pageNum >= 0 && pageNum < pageCount) {
      candidates.add(pageNum);
    }
  }
  return [...candidates];
}

type PdfViewportZoomAnchor = {
  type: 'viewport';
  localX: number;
  localY: number;
  ratioX: number;
  ratioY: number;
  /** scrollHeight at commit time; a different height = re-measure landed. */
  commitScrollHeight?: number;
};

type PdfPageZoomAnchor = {
  type: 'page';
  pageIndex: number;
  pageX: number;
  pageY: number;
  clientX: number;
  clientY: number;
  /**
   * Page frame size the committed zoom will produce. Virtuoso re-renders item
   * content asynchronously after the zoom commit, so the correction must wait
   * until the anchor page actually has this size — measuring earlier sees the
   * old layout and reports a false zero drift.
   */
  expectedPageWidth?: number;
  expectedPageHeight?: number;
};

type PdfZoomScrollAnchor = PdfViewportZoomAnchor | PdfPageZoomAnchor;

type PdfSignaturePlacementDrag = {
  placementId: string;
  mode: 'move' | 'resize';
  startClientX: number;
  startClientY: number;
  pageWidth: number;
  pageHeight: number;
  startRect: { x: number; y: number; width: number; height: number };
};

/**
 * Resolved initial viewport target for the fullscreen PDF viewer, computed once
 * synchronously at mount: either an explicit navigation hit (cmdk search,
 * `isNav: true`) or the persisted per-browser free position. The page landing
 * is handed to Virtuoso's `initialTopMostItemIndex` so it renders from that
 * index on the first paint — avoiding the measure-then-scrollToIndex race where
 * `scrollToIndex` fires before the list has measured its item heights.
 */
interface PdfFullscreenViewportTarget {
  pageIndex: number;
  scrollLeft: number;
  scrollTop: number;
  zoom: number;
  isNav: boolean;
  /** Whether a navigation hit was already consumed (bookmarked/stale URL). */
  stale: boolean;
  /** Navigation request id to persist so a reopened URL is detected as stale. */
  consumedRequestId?: string;
  navigationKey: string | null;
}

function resolveInitialFullscreenTarget({
  source,
  navigation,
  pageCount,
}: {
  source: AttachmentViewerSource;
  navigation: PDFViewerNavigation | undefined;
  pageCount: number;
}): PdfFullscreenViewportTarget {
  const persisted = readPdfFullscreenViewportState({
    key: createPdfFullscreenStorageKey(source),
    maxPageIndex: Math.max(0, pageCount - 1),
    minZoom: PDF_ZOOM_MIN,
    maxZoom: PDF_ZOOM_MAX,
  });
  const navigationKey = createPDFViewerNavigationKey(navigation);
  const requestId =
    typeof navigation?.requestId === 'string' ||
    typeof navigation?.requestId === 'number'
      ? String(navigation.requestId)
      : null;
  const navPageIndex = navigationKey
    ? normalizePDFPageIndex(navigation?.pageIndex, pageCount)
    : null;
  const navAlreadyConsumed =
    !!requestId && persisted?.consumedRequestId === requestId;

  if (navPageIndex !== null && !navAlreadyConsumed) {
    // Fresh navigation hit → land on the searched page (Story 2). Zoom is not
    // restored for navigation; persist the request id so a reopened/bookmarked
    // URL is treated as stale next time (Story 2b).
    return {
      pageIndex: navPageIndex,
      scrollLeft: 0,
      scrollTop: 0,
      zoom: 1,
      isNav: true,
      stale: false,
      consumedRequestId: requestId ?? undefined,
      navigationKey,
    };
  }

  // Stale or absent navigation → restore the persisted free position (Story 1 +
  // Story 2b: reopening without a fresh search lands on the last free position,
  // never an old search hit).
  if (persisted) {
    return {
      pageIndex: persisted.pageIndex,
      scrollLeft: persisted.scrollLeft,
      scrollTop: persisted.scrollTop,
      zoom: persisted.zoom,
      isNav: false,
      stale: navAlreadyConsumed,
      consumedRequestId: persisted.consumedRequestId,
      navigationKey,
    };
  }

  return {
    pageIndex: 0,
    scrollLeft: 0,
    scrollTop: 0,
    zoom: 1,
    isNav: false,
    stale: navAlreadyConsumed,
    consumedRequestId: navAlreadyConsumed
      ? (requestId ?? undefined)
      : undefined,
    navigationKey,
  };
}

type PdfSelectionDraft = {
  quote: string;
  anchors: CommentTargetAnchor[];
  toolbar: {
    left: number;
    top: number;
  };
  dragging?: boolean;
};

type PdfSelectedHighlight = {
  id: string;
  toolbar: {
    left: number;
    top: number;
  };
};

type PdfZoomAnchor = {
  clientX?: number;
  clientY?: number;
};

type PdfMarkupExportOptions = {
  includeAnnotations: boolean;
  includeComments: boolean;
};

const PdfPrintOptionsContent = ({
  hasAnnotations,
  hasComments,
  initialOptions,
  onChange,
}: {
  hasAnnotations: boolean;
  hasComments: boolean;
  initialOptions: PdfMarkupExportOptions;
  onChange: (options: PdfMarkupExportOptions) => void;
}) => {
  const t = useI18n();
  const [options, setOptions] = useState(initialOptions);
  const updateOptions = useCallback(
    (next: PdfMarkupExportOptions) => {
      setOptions(next);
      onChange(next);
    },
    [onChange]
  );

  return (
    <div className={styles.pdfPrintOptions}>
      <label className={styles.pdfPrintOption} data-disabled={!hasComments}>
        <Checkbox
          checked={options.includeComments}
          disabled={!hasComments}
          onChange={(_, checked) => {
            if (!hasComments) return;
            updateOptions({ ...options, includeComments: checked });
          }}
          name="pdf-print-comments"
        />
        <span className={styles.pdfPrintOptionText}>
          <span className={styles.pdfPrintOptionTitle}>
            {t['com.streamlain.pdf.print.option.comments']()}
          </span>
          <span className={styles.pdfPrintOptionDescription}>
            {t['com.streamlain.pdf.print.option.comments.description']()}
          </span>
        </span>
      </label>
      <label className={styles.pdfPrintOption} data-disabled={!hasAnnotations}>
        <Checkbox
          checked={options.includeAnnotations}
          disabled={!hasAnnotations}
          onChange={(_, checked) => {
            if (!hasAnnotations) return;
            updateOptions({ ...options, includeAnnotations: checked });
          }}
          name="pdf-print-annotations"
        />
        <span className={styles.pdfPrintOptionText}>
          <span className={styles.pdfPrintOptionTitle}>
            {t['com.streamlain.pdf.print.option.annotations']()}
          </span>
          <span className={styles.pdfPrintOptionDescription}>
            {t['com.streamlain.pdf.print.option.annotations.description']()}
          </span>
        </span>
      </label>
    </div>
  );
};

type PdfGlyphPointer = {
  pageIndex: number;
  glyphIndex: number;
};

function calculatePageNum(el: HTMLElement, pageCount: number) {
  const viewportBounds = el.getBoundingClientRect();
  const viewportCenter = viewportBounds.top + viewportBounds.height / 2;
  let bestVisiblePage: {
    distance: number;
    index: number;
    visibleHeight: number;
  } | null = null;

  for (const frame of el.querySelectorAll<HTMLElement>(
    PDF_PAGE_FRAME_SELECTOR
  )) {
    const pageIndex = Number(frame.dataset.pdfPageFrameIndex);
    if (!Number.isInteger(pageIndex) || pageIndex < 0 || pageIndex >= pageCount)
      continue;

    const bounds = frame.getBoundingClientRect();
    const visibleHeight =
      Math.min(bounds.bottom, viewportBounds.bottom) -
      Math.max(bounds.top, viewportBounds.top);
    if (visibleHeight <= 0) continue;

    const distance = Math.abs(bounds.top + bounds.height / 2 - viewportCenter);
    if (
      !bestVisiblePage ||
      visibleHeight > bestVisiblePage.visibleHeight ||
      (visibleHeight === bestVisiblePage.visibleHeight &&
        distance < bestVisiblePage.distance)
    ) {
      bestVisiblePage = { distance, index: pageIndex, visibleHeight };
    }
  }

  if (bestVisiblePage) return bestVisiblePage.index;

  const { scrollTop, scrollHeight } = el;
  const pageHeight = scrollHeight / pageCount;
  const n = scrollTop / pageHeight;
  const t = n / pageCount;
  const index = Math.floor(n + t);
  const cursor = Math.min(index, pageCount - 1);
  return cursor;
}

function clampUnit(value: number) {
  return Math.max(0, Math.min(1, value));
}

function clampPdfZoom(value: number) {
  return Math.min(PDF_ZOOM_MAX, Math.max(PDF_ZOOM_MIN, value));
}

function clampThumbnailPanelWidth(value: number) {
  return Math.min(
    PDF_THUMBNAIL_PANEL_MAX_WIDTH,
    Math.max(PDF_THUMBNAIL_PANEL_MIN_WIDTH, value)
  );
}

function getPdfPageFrameAtPoint({
  root,
  clientX,
  clientY,
}: {
  root: HTMLElement;
  clientX: number;
  clientY: number;
}) {
  const directFrame = document
    .elementsFromPoint(clientX, clientY)
    .find(
      (element): element is HTMLElement =>
        element instanceof HTMLElement &&
        Boolean(element.closest(PDF_PAGE_FRAME_SELECTOR))
    )
    ?.closest<HTMLElement>(PDF_PAGE_FRAME_SELECTOR);
  if (directFrame && root.contains(directFrame)) return directFrame;

  return (
    Array.from(
      root.querySelectorAll<HTMLElement>(PDF_PAGE_FRAME_SELECTOR)
    ).find(frame => {
      const bounds = frame.getBoundingClientRect();
      return (
        clientX >= bounds.left &&
        clientX <= bounds.right &&
        clientY >= bounds.top &&
        clientY <= bounds.bottom
      );
    }) ?? null
  );
}

function formatPdfHighlightColorLabel(colorId: string) {
  return colorId.charAt(0).toUpperCase() + colorId.slice(1);
}

function readGlyphPointer(glyph: HTMLElement): PdfGlyphPointer | null {
  const pageLayer = glyph.closest<HTMLElement>(PDF_PAGE_LAYER_SELECTOR);
  if (!pageLayer) return null;
  const pageIndex = Number(pageLayer.dataset.pdfPageIndex);
  const glyphIndex = Number(glyph.dataset.pdfGlyphIndex);
  if (!Number.isFinite(pageIndex) || !Number.isFinite(glyphIndex)) return null;
  return { pageIndex, glyphIndex };
}

function getPdfGlyphAtPoint({
  root,
  clientX,
  clientY,
  allowNearest,
}: {
  root: HTMLElement;
  clientX: number;
  clientY: number;
  allowNearest: boolean;
}): PdfGlyphPointer | null {
  const directGlyph = document
    .elementsFromPoint(clientX, clientY)
    .find(
      (element): element is HTMLElement =>
        element instanceof HTMLElement &&
        Boolean(element.closest(PDF_GLYPH_SELECTOR))
    )
    ?.closest<HTMLElement>(PDF_GLYPH_SELECTOR);
  if (directGlyph && root.contains(directGlyph)) {
    return readGlyphPointer(directGlyph);
  }

  const pageLayer =
    document
      .elementsFromPoint(clientX, clientY)
      .find(
        (element): element is HTMLElement =>
          element instanceof HTMLElement &&
          Boolean(element.closest(PDF_PAGE_LAYER_SELECTOR))
      )
      ?.closest<HTMLElement>(PDF_PAGE_LAYER_SELECTOR) ??
    Array.from(
      root.querySelectorAll<HTMLElement>(PDF_PAGE_LAYER_SELECTOR)
    ).find(layer => {
      const bounds = layer.getBoundingClientRect();
      return (
        clientX >= bounds.left &&
        clientX <= bounds.right &&
        clientY >= bounds.top &&
        clientY <= bounds.bottom
      );
    });
  if (!pageLayer || !root.contains(pageLayer)) return null;

  let best: { glyph: HTMLElement; distance: number; vertical: number } | null =
    null;
  for (const glyph of pageLayer.querySelectorAll<HTMLElement>(
    PDF_GLYPH_SELECTOR
  )) {
    const bounds = glyph.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0) continue;
    const centerY = bounds.top + bounds.height / 2;
    const dx =
      clientX < bounds.left
        ? bounds.left - clientX
        : clientX > bounds.right
          ? clientX - bounds.right
          : 0;
    const dy =
      clientY < bounds.top
        ? bounds.top - clientY
        : clientY > bounds.bottom
          ? clientY - bounds.bottom
          : 0;
    const distance = dx * dx + dy * dy;
    const vertical = Math.abs(centerY - clientY);
    if (!best || distance < best.distance) {
      best = { glyph, distance, vertical };
    }
    if (clientX >= bounds.left && clientX <= bounds.right && vertical <= 8) {
      best = { glyph, distance: 0, vertical };
      break;
    }
  }
  if (!best) return null;
  if (!allowNearest && best.vertical > 14) return null;
  return readGlyphPointer(best.glyph);
}

function comparePdfGlyphPointer(a: PdfGlyphPointer, b: PdfGlyphPointer) {
  if (a.pageIndex !== b.pageIndex) return a.pageIndex - b.pageIndex;
  return a.glyphIndex - b.glyphIndex;
}

function normalizePdfText(text: string) {
  return text.replace(/\s+/g, ' ').trim();
}

function mergeGlyphRects({
  glyphs,
  pageBounds,
}: {
  glyphs: HTMLElement[];
  pageBounds: DOMRect;
}) {
  type Line = {
    centerY: number;
    height: number;
    glyphs: { text: string; bounds: DOMRect }[];
  };
  const lines: Line[] = [];

  const sortedGlyphs = glyphs
    .map(glyph => ({
      text: glyph.textContent ?? '',
      bounds: glyph.getBoundingClientRect(),
    }))
    .filter(({ bounds }) => bounds.width > 0 && bounds.height > 0)
    .sort(
      (a, b) => a.bounds.top - b.bounds.top || a.bounds.left - b.bounds.left
    );

  for (const glyph of sortedGlyphs) {
    const centerY = glyph.bounds.top + glyph.bounds.height / 2;
    const line = lines.find(
      line => Math.abs(line.centerY - centerY) <= Math.max(3, line.height * 0.7)
    );
    if (line) {
      line.glyphs.push(glyph);
      line.centerY =
        (line.centerY * (line.glyphs.length - 1) + centerY) /
        line.glyphs.length;
      line.height = Math.max(line.height, glyph.bounds.height);
    } else {
      lines.push({
        centerY,
        height: glyph.bounds.height,
        glyphs: [glyph],
      });
    }
  }

  const rects = lines.flatMap(line => {
    const sortedLineGlyphs = line.glyphs.sort(
      (a, b) => a.bounds.left - b.bounds.left
    );
    const groups: (typeof sortedLineGlyphs)[] = [];
    for (const glyph of sortedLineGlyphs) {
      const current = groups[groups.length - 1];
      const previous = current?.[current.length - 1];
      const gap = previous ? glyph.bounds.left - previous.bounds.right : 0;
      if (!current || gap > Math.max(18, line.height * 2.4)) {
        groups.push([glyph]);
      } else {
        current.push(glyph);
      }
    }
    return groups.map(group => {
      const left = Math.min(...group.map(item => item.bounds.left));
      const top = Math.min(...group.map(item => item.bounds.top));
      const right = Math.max(...group.map(item => item.bounds.right));
      const bottom = Math.max(...group.map(item => item.bounds.bottom));
      return {
        x: clampUnit((left - pageBounds.left) / pageBounds.width),
        y: clampUnit((top - pageBounds.top) / pageBounds.height),
        width: clampUnit((right - left) / pageBounds.width),
        height: clampUnit((bottom - top) / pageBounds.height),
      };
    });
  });

  const quote = normalizePdfText(
    lines
      .sort((a, b) => a.centerY - b.centerY)
      .map(line =>
        line.glyphs
          .sort((a, b) => a.bounds.left - b.bounds.left)
          .map(glyph => glyph.text)
          .join('')
      )
      .join('\n')
  );

  return { rects, quote };
}

function getPdfSelectionFromGlyphRange({
  root,
  source,
  start,
  end,
  toolbar,
  color,
}: {
  root: HTMLElement;
  source: AttachmentViewerSource;
  start: PdfGlyphPointer;
  end: PdfGlyphPointer;
  toolbar: { left: number; top: number };
  color: string;
}): PdfSelectionDraft | null {
  const sourceId = source.blobId;
  if (!sourceId) return null;
  const [from, to] =
    comparePdfGlyphPointer(start, end) <= 0 ? [start, end] : [end, start];
  const pageLayers = Array.from(
    root.querySelectorAll<HTMLElement>(PDF_PAGE_LAYER_SELECTOR)
  ).sort(
    (a, b) => Number(a.dataset.pdfPageIndex) - Number(b.dataset.pdfPageIndex)
  );
  const anchors: CommentTargetAnchor[] = [];
  const quotes: string[] = [];

  for (const pageLayer of pageLayers) {
    const rawPageIndex = pageLayer.dataset.pdfPageIndex;
    if (rawPageIndex === undefined) continue;
    const pageIndex = Number(rawPageIndex);
    if (!Number.isFinite(pageIndex)) continue;
    if (pageIndex < from.pageIndex || pageIndex > to.pageIndex) continue;
    const glyphs = Array.from(
      pageLayer.querySelectorAll<HTMLElement>(PDF_GLYPH_SELECTOR)
    ).filter(glyph => {
      const glyphIndex = Number(glyph.dataset.pdfGlyphIndex);
      if (!Number.isFinite(glyphIndex)) return false;
      if (pageIndex === from.pageIndex && glyphIndex < from.glyphIndex) {
        return false;
      }
      if (pageIndex === to.pageIndex && glyphIndex > to.glyphIndex) {
        return false;
      }
      return true;
    });
    if (glyphs.length === 0) continue;

    const { rects, quote } = mergeGlyphRects({
      glyphs,
      pageBounds: pageLayer.getBoundingClientRect(),
    });
    if (rects.length === 0 || !quote) continue;
    quotes.push(quote);
    anchors.push({
      type: 'pdf-text',
      blockId: source.id,
      sourceId,
      pageIndex,
      quote,
      rects,
      color,
    });
  }

  const quote = normalizePdfText(quotes.join('\n'));
  if (anchors.length === 0 || !quote) return null;

  return {
    quote,
    anchors: anchors.map(anchor => ({
      ...anchor,
      quote,
    })),
    toolbar: getPdfToolbarPosition(root, toolbar.left, toolbar.top, 52),
  };
}

function getPdfToolbarPosition(
  root: HTMLElement,
  clientX: number,
  clientY: number,
  offsetY: number
) {
  const bounds = root.getBoundingClientRect();
  return {
    left: Math.min(
      Math.max(48, bounds.width - 48),
      Math.max(48, clientX - bounds.left)
    ),
    top: Math.min(
      Math.max(12, bounds.height - 44),
      Math.max(12, clientY - bounds.top - offsetY)
    ),
  };
}

function getPdfSelectionHighlightRects({
  selection,
  pageIndex,
}: {
  selection: PdfSelectionDraft | null;
  pageIndex: number;
}): PDFPageHighlightRect[] {
  if (!selection) return [];
  return selection.anchors.flatMap(anchor => {
    if (anchor.type !== 'pdf-text' || anchor.pageIndex !== pageIndex) return [];
    return anchor.rects.map(rect => ({
      ...rect,
      color: PDF_SELECTION_PREVIEW_COLOR,
      kind: 'selection' as const,
    }));
  });
}

/**
 * Editable page number in the thumbnail sidebar header. Shows the current page
 * while idle; typing a number and pressing Enter jumps to that page (clamped to
 * the document range). Escape or blur discards the draft.
 */
function PdfThumbnailsPageInput({
  current,
  pageCount,
  onJump,
}: {
  current: number;
  pageCount: number;
  onJump: (pageIndex: number) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);

  const commit = useCallback(() => {
    if (draft === null) return;
    const parsed = Number.parseInt(draft, 10);
    setDraft(null);
    if (!Number.isInteger(parsed) || pageCount <= 0) return;
    const clamped = Math.min(Math.max(parsed, 1), pageCount);
    onJump(clamped - 1);
  }, [draft, onJump, pageCount]);

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      // Don't let viewer-level shortcuts (page nav, etc.) react while typing.
      event.stopPropagation();
      if (event.key === 'Enter') {
        event.preventDefault();
        commit();
        event.currentTarget.blur();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        setDraft(null);
        event.currentTarget.blur();
      }
    },
    [commit]
  );

  const onChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setDraft(event.target.value.replace(/[^0-9]/g, ''));
  }, []);

  const onFocus = useCallback(
    (event: ReactFocusEvent<HTMLInputElement>) => {
      setDraft(String(current));
      event.currentTarget.select();
    },
    [current]
  );

  return (
    <input
      className={clsx('page-cursor', styles.pdfThumbnailsPageInput)}
      type="text"
      inputMode="numeric"
      aria-label="Seitenzahl eingeben"
      size={Math.max(1, String(Math.max(pageCount, 1)).length)}
      value={draft ?? String(current)}
      onChange={onChange}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      onBlur={() => setDraft(null)}
    />
  );
}

export interface PDFViewerInnerProps {
  source: AttachmentViewerSource;
  model: AttachmentViewerBaseProps['model'];
  std?: AttachmentViewerBaseProps['std'];
  pdf: PDF;
  meta: PDFMeta;
  navigation?: PDFViewerNavigation;
  onClose?: () => void;
  hideThumbnails?: boolean;
}

export const PDFViewerInner = ({
  pdf,
  meta,
  navigation,
  source,
  model,
  std,
  onClose,
  hideThumbnails = false,
}: PDFViewerInnerProps) => {
  // Resolve the initial viewport target once at mount (sync) so it can seed
  // Virtuoso's initialTopMostItemIndex on the first paint.
  const [initialFullscreenTarget] = useState(() =>
    resolveInitialFullscreenTarget({
      source,
      navigation,
      pageCount: meta.pageCount,
    })
  );
  const pdfEventId = source.id;
  const [cursor, setCursor] = useState(initialFullscreenTarget.pageIndex);
  const [collapsed, setCollapsed] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [thumbnailOverlayMode, setThumbnailOverlayMode] = useState(false);
  const [zoom, setZoom] = useState(initialFullscreenTarget.zoom);
  const [thumbnailPanelWidth, setThumbnailPanelWidth] = useState(160);
  const sidebarLayoutWidth =
    hideThumbnails || collapsed || thumbnailOverlayMode
      ? 0
      : thumbnailPanelWidth;
  const [viewportInfo, setViewportInfo] = useState({ width: 0, height: 0 });
  const [findState, dispatchFind] = useReducer(
    pdfFindReducer,
    INITIAL_PDF_FIND_STATE
  );
  // Always-current snapshot of the find state, read by navigation effects that
  // must not list `findState` in their deps (would re-fire on every result
  // update and clobber the set).
  const findStateRef = useRef(findState);
  findStateRef.current = findState;
  const workspaceService = useService(WorkspaceService);
  const workspaceServerService = useService(WorkspaceServerService);
  const workspaceDialogService = useService(WorkspaceDialogService);
  const workbenchService = useService(WorkbenchService);
  const mailComposeHandoff = useService(MailComposeHandoffService);
  const mailGate = usePlanGate(WORKSPACE_MAIL_FEATURE_KEYS);
  const digitalSignatureGate = usePlanGate(PDF_DIGITAL_SIGNATURE_FEATURE_KEYS);
  const canOpenWorkspacePlans = useCanOpenWorkspacePlans();
  const authService = useService(AuthService);
  const avatarCache = useService(AvatarAssetCacheService);
  const serverService = useService(ServerService);
  const account = useLiveData(authService.session.account$);
  const workspaceInfo = useWorkspaceInfo(workspaceService.workspace);
  const localPdfTextProvider = useMemo(
    () => new PdfDocumentTextProvider(pdf),
    [pdf]
  );
  const t = useI18n();
  const { openConfirmModal } = useConfirmModal();
  const coverage = usePdfSearchCoverage({ source });
  const commentManager = useService(DocCommentManagerService);
  const commentPanelService = useService(CommentPanelService);
  const [commentEntity, setCommentEntity] = useState<DocCommentEntity | null>(
    null
  );
  const comments = useLiveData(
    commentEntity?.comments$ ?? new LiveData<DocComment[]>([])
  );
  const highlightedCommentId = useLiveData(
    commentEntity?.commentHighlighted$ ?? new LiveData<string | null>(null)
  );
  const [pdfSelection, setPdfSelection] = useState<PdfSelectionDraft | null>(
    null
  );
  const [selectedPdfHighlight, setSelectedPdfHighlight] =
    useState<PdfSelectedHighlight | null>(null);
  const pdfSourceId = source.blobId ?? '';
  const storedHighlights = useStoredPdfTextHighlights(model);
  const isCloudWorkspace = workspaceService.workspace.flavour !== 'local';
  const canUsePdfSignatures = Boolean(
    isCloudWorkspace &&
    pdfSourceId &&
    workspaceInfo &&
    workspaceInfo.role !== Permission.Reader
  );
  const signatures = useWorkspacePdfSignatures(
    pdfSourceId || null,
    canUsePdfSignatures
  );
  const signedVersionState = useSignedPdfVersionForBlob(
    pdfSourceId || null,
    Boolean(isCloudWorkspace && pdfSourceId)
  );
  const signedPdfVersion = signedVersionState.signedVersion;
  const isSignedPdfCopy = Boolean(signedPdfVersion);
  const canEditPdfSignatureLayer = canUsePdfSignatures && !isSignedPdfCopy;
  const canManageSignatureServices =
    workspaceInfo?.role === Permission.Admin ||
    workspaceInfo?.role === Permission.Owner;
  // Sending the PDF by mail needs the workspace's mail: a cloud workspace with
  // mail in its plan and a member who may write there.
  const canSendPdfAsMail = Boolean(
    isCloudWorkspace &&
    pdfSourceId &&
    mailGate.status === 'allowed' &&
    workspaceInfo &&
    workspaceInfo.role !== Permission.Reader
  );
  // The way to a digitally signed copy, one step at a time: the panel always
  // names what is still missing and leads there.
  const digitalSigningStep = useMemo(
    () =>
      canEditPdfSignatureLayer &&
      !signatures.isLoading &&
      digitalSignatureGate.status !== 'loading'
        ? resolveDigitalSigningStep({
            connections: signatures.connections,
            certificates: signatures.certificates,
            profiles: signatures.profiles,
            policy: signatures.policy,
            isAdmin: canManageSignatureServices,
            planLock:
              digitalSignatureGate.status === 'locked'
                ? digitalSignatureGate
                : null,
          })
        : ({ kind: 'hidden' } as const),
    [
      canEditPdfSignatureLayer,
      canManageSignatureServices,
      digitalSignatureGate,
      signatures.certificates,
      signatures.isLoading,
      signatures.policy,
      signatures.profiles,
      signatures.connections,
    ]
  );
  const canSignDigitally = digitalSigningStep.kind !== 'hidden';
  const signaturePlacements = useStoredPdfSignaturePlacements(model);
  const [signaturePanelOpen, setSignaturePanelOpen] = useState(false);
  const [signaturePanelMode, setSignaturePanelMode] =
    useState<PdfSignaturePanelMode>('visual');
  const [selectedSignatureAppearanceId, setSelectedSignatureAppearanceId] =
    useState('');
  const [armedSignatureAppearanceId, setArmedSignatureAppearanceId] =
    useState('');
  const [signatureCursorPreview, setSignatureCursorPreview] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  const [selectedSigningProfileId, setSelectedSigningProfileId] = useState('');
  const [readerSignatureCreatorOpen, setReaderSignatureCreatorOpen] =
    useState(false);
  const [signingJob, setSigningJob] = useState<PdfSigningJobInfo | null>(null);
  const [placingSignedVersionId, setPlacingSignedVersionId] = useState<
    string | null
  >(null);
  const [
    includeAnnotationsInDigitalSignature,
    setIncludeAnnotationsInDigitalSignature,
  ] = useState(false);
  const [isPreparingSigningSnapshot, setIsPreparingSigningSnapshot] =
    useState(false);
  const [selectedSignaturePlacementId, setSelectedSignaturePlacementId] =
    useState<string | null>(null);

  useEffect(() => {
    if (!canEditPdfSignatureLayer && signaturePanelOpen) {
      setSignaturePanelOpen(false);
    }
    // A signed copy keeps its signing tools in sight, locked with the reason.
    const detail = {
      blockId: pdfEventId,
      available: canEditPdfSignatureLayer,
      open: canEditPdfSignatureLayer && signaturePanelOpen,
      mode: signaturePanelMode,
      digital: canSignDigitally,
      lockedReason:
        canUsePdfSignatures && isSignedPdfCopy ? 'signed_copy' : undefined,
    };
    const dispatchState = () => {
      window.dispatchEvent(
        new CustomEvent(PDF_SIGNATURE_STATE_EVENT, {
          detail,
        })
      );
    };
    dispatchState();
    const frame = window.requestAnimationFrame(dispatchState);
    return () => window.cancelAnimationFrame(frame);
  }, [
    canEditPdfSignatureLayer,
    canSignDigitally,
    canUsePdfSignatures,
    isSignedPdfCopy,
    pdfEventId,
    signaturePanelMode,
    signaturePanelOpen,
  ]);

  useEffect(() => {
    // The digital panel closes its mode once there is nothing left to show.
    if (signaturePanelMode === 'digital' && !canSignDigitally) {
      setSignaturePanelMode('visual');
    }
  }, [canSignDigitally, signaturePanelMode]);

  useEffect(() => {
    const onSignatureToggle = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          blockId?: string;
          mode?: PdfSignaturePanelMode;
        }>
      ).detail;
      if (detail?.blockId !== pdfEventId || !canEditPdfSignatureLayer) return;
      if (detail.mode === 'digital' && !canSignDigitally) return;
      setArmedSignatureAppearanceId('');
      setSignatureCursorPreview(null);
      if (detail.mode === 'digital' || detail.mode === 'visual') {
        setSignaturePanelMode(detail.mode);
        setSignaturePanelOpen(true);
        return;
      }
      setSignaturePanelOpen(open => !open);
    };
    window.addEventListener(PDF_SIGNATURE_TOGGLE_EVENT, onSignatureToggle);
    return () => {
      window.removeEventListener(PDF_SIGNATURE_TOGGLE_EVENT, onSignatureToggle);
    };
  }, [canEditPdfSignatureLayer, canSignDigitally, pdfEventId]);

  const usableSignatureAppearances = useMemo(
    () =>
      signatures.appearances.filter(
        appearance => appearance.status === 'active' && appearance.canUse
      ),
    [signatures.appearances]
  );
  const signatureLoadErrorMessage = useMemo(
    () =>
      signatures.loadError
        ? getPdfSignatureErrorMessage(signatures.loadError, 'load')
        : null,
    [signatures.loadError]
  );
  const armedSignatureAppearance = useMemo(
    () =>
      usableSignatureAppearances.find(
        appearance => appearance.id === armedSignatureAppearanceId
      ) ?? null,
    [armedSignatureAppearanceId, usableSignatureAppearances]
  );
  const readySigningProfiles = useMemo(
    () =>
      digitalSigningStep.kind === 'ready' ? digitalSigningStep.profiles : [],
    [digitalSigningStep]
  );
  // The default profile leads the list and signs unless another is picked.
  const selectedSigningProfile = useMemo(
    () =>
      readySigningProfiles.find(
        profile => profile.id === selectedSigningProfileId
      ) ??
      readySigningProfiles[0] ??
      null,
    [readySigningProfiles, selectedSigningProfileId]
  );
  const completedSigningVersion = useMemo(() => {
    if (signingJob?.status !== 'completed') return null;
    return (
      signatures.signedVersions.find(
        version => version.signingJobId === signingJob.id
      ) ?? null
    );
  }, [signatures.signedVersions, signingJob]);
  const announcedSigningJobIdRef = useRef<string | null>(null);
  useEffect(() => {
    // The outcome is announced once: the copy exists and where it is kept.
    if (!completedSigningVersion?.signingJobId) return;
    if (
      announcedSigningJobIdRef.current === completedSigningVersion.signingJobId
    ) {
      return;
    }
    announcedSigningJobIdRef.current = completedSigningVersion.signingJobId;
    notify.success({
      title: t['com.streamlain.pdf.signature.toast.signedCopyCreated']({
        name: formatSignedPdfVersionName(completedSigningVersion),
      }),
    });
  }, [completedSigningVersion, t]);
  const canPlaceSignedCopies = Boolean(model);
  const relevantSignaturePlacements = useMemo(
    () =>
      signaturePlacements.filter(
        placement => placement.sourceId === pdfSourceId
      ),
    [pdfSourceId, signaturePlacements]
  );
  const currentSignaturePlacement = useMemo(() => {
    if (!selectedSignatureAppearanceId) return null;
    return (
      relevantSignaturePlacements.find(
        placement =>
          placement.signatureAppearanceId === selectedSignatureAppearanceId
      ) ?? null
    );
  }, [relevantSignaturePlacements, selectedSignatureAppearanceId]);
  const openSigningProfileSettings = useCallback(() => {
    workspaceDialogService.open('setting', {
      activeTab: 'pdf-signatures',
      scrollAnchor: 'digital-signature',
    });
  }, [workspaceDialogService]);
  const openSignatureServiceSettings = useCallback(() => {
    workspaceDialogService.open('setting', {
      activeTab: 'workspace:pdf-signatures',
      scrollAnchor: 'signature-services',
    });
  }, [workspaceDialogService]);
  const openWorkspacePlans = useCallback(() => {
    workspaceDialogService.open('setting', { activeTab: 'workspace:plans' });
  }, [workspaceDialogService]);
  const selectedSignaturePlacement = useMemo(() => {
    if (!selectedSignaturePlacementId) return null;
    return (
      relevantSignaturePlacements.find(
        placement => placement.id === selectedSignaturePlacementId
      ) ?? null
    );
  }, [relevantSignaturePlacements, selectedSignaturePlacementId]);

  useEffect(() => {
    if (!selectedSignaturePlacementId) return;
    if (
      relevantSignaturePlacements.some(
        placement => placement.id === selectedSignaturePlacementId
      )
    ) {
      return;
    }
    setSelectedSignaturePlacementId(null);
  }, [relevantSignaturePlacements, selectedSignaturePlacementId]);

  const resolveReaderSignatureClipboardBlob = useCallback(
    async (blobId: string) => {
      if (!model) return null;
      return model.store.blobSync.get(blobId);
    },
    [model]
  );

  const createReaderSignatureAppearance = useCallback(
    async ({ file, suggestedName }: PdfSignatureCreatorResult) => {
      if (!canEditPdfSignatureLayer) return false;
      try {
        const fallbackName = t['com.streamlain.pdf.signature.upload.autoName']({
          timestamp: formatSignatureTimestamp(),
        });
        const appearance = await signatures.uploadAppearance({
          name:
            suggestedName ||
            getPdfSignatureFileNameWithoutExtension(file.name) ||
            fallbackName,
          type: 'personal_signature',
          file,
        });
        setSelectedSignatureAppearanceId(appearance.id);
        setArmedSignatureAppearanceId(appearance.id);
        setReaderSignatureCreatorOpen(false);
        setSignaturePanelOpen(false);
        setSignatureCursorPreview(null);
        return true;
      } catch (error) {
        console.error('Failed to upload PDF signature appearance', error);
        return false;
      }
    },
    [canEditPdfSignatureLayer, signatures, t]
  );

  const armSignatureAppearance = useCallback(
    (appearanceId: string) => {
      if (!canEditPdfSignatureLayer) return;
      setSelectedSignatureAppearanceId(appearanceId);
      setArmedSignatureAppearanceId(appearanceId);
      setSelectedSignaturePlacementId(null);
      setReaderSignatureCreatorOpen(false);
      setSignaturePanelOpen(false);
      setSignatureCursorPreview(null);
    },
    [canEditPdfSignatureLayer]
  );

  const cancelArmedSignaturePlacement = useCallback(() => {
    setArmedSignatureAppearanceId('');
    setSignatureCursorPreview(null);
  }, []);

  useEffect(() => {
    if (!signingJob) return;
    if (
      signingJob.status !== 'queued' &&
      signingJob.status !== 'running' &&
      signingJob.status !== 'finalizing'
    ) {
      return;
    }
    let disposed = false;
    const poll = async () => {
      try {
        const next = await signatures.getSigningJob(signingJob.id);
        if (disposed) return;
        setSigningJob(next);
        if (next.status === 'completed' || next.status === 'failed') {
          await signatures.refresh();
        }
      } catch {
        // Keep the current job state visible; the next interval retries.
      }
    };
    const interval = window.setInterval(() => {
      void poll();
    }, 2000);
    void poll();
    return () => {
      disposed = true;
      window.clearInterval(interval);
    };
  }, [signatures, signingJob]);

  const startSignaturePlacementDrag = useCallback(
    (
      event: ReactPointerEvent<HTMLElement>,
      placementId: string,
      mode: PdfSignaturePlacementDrag['mode']
    ) => {
      if (!canEditPdfSignatureLayer) return;
      const placement = relevantSignaturePlacements.find(
        placement => placement.id === placementId
      );
      if (!placement) return;
      const placementElement = (
        event.currentTarget as HTMLElement
      ).closest<HTMLElement>('[data-pdf-signature-placement-id]');
      const pageElement = placementElement?.closest<HTMLElement>(
        '[data-pdf-page-index]'
      );
      const pageBounds = pageElement?.getBoundingClientRect();
      if (!placementElement || !pageBounds) return;
      event.preventDefault();
      event.stopPropagation();
      placementElement.focus({ preventScroll: true });
      placementElement.setPointerCapture(event.pointerId);
      setSelectedSignatureAppearanceId(placement.signatureAppearanceId ?? '');
      setSelectedSignaturePlacementId(placement.id);
      signaturePlacementDragRef.current = {
        placementId: placement.id,
        mode,
        startClientX: event.clientX,
        startClientY: event.clientY,
        pageWidth: Math.max(1, pageBounds.width),
        pageHeight: Math.max(1, pageBounds.height),
        startRect: placement.rect,
      };
    },
    [canEditPdfSignatureLayer, relevantSignaturePlacements]
  );

  const updateSignaturePlacementDrag = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const drag = signaturePlacementDragRef.current;
      if (!drag) return;
      event.preventDefault();
      event.stopPropagation();
      const dx = (event.clientX - drag.startClientX) / drag.pageWidth;
      const dy = (event.clientY - drag.startClientY) / drag.pageHeight;
      const rect =
        drag.mode === 'resize'
          ? {
              ...drag.startRect,
              width: drag.startRect.width + dx,
              height: drag.startRect.height + dy,
            }
          : {
              ...drag.startRect,
              x: drag.startRect.x + dx,
              y: drag.startRect.y + dy,
            };
      updatePdfSignaturePlacement({
        model,
        placementId: drag.placementId,
        patch: { rect },
      });
    },
    [model]
  );

  const finishSignaturePlacementDrag = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const placementElement = (
        event.currentTarget as HTMLElement
      ).closest<HTMLElement>('[data-pdf-signature-placement-id]');
      if (placementElement?.hasPointerCapture(event.pointerId)) {
        placementElement.releasePointerCapture(event.pointerId);
      }
      signaturePlacementDragRef.current = null;
      event.stopPropagation();
    },
    []
  );

  const onSignaturePlacementKeyDown = useCallback(
    (
      event: ReactKeyboardEvent<HTMLDivElement>,
      placementId: string,
      rect: { x: number; y: number; width: number; height: number }
    ) => {
      const delta = event.shiftKey ? 0.02 : 0.005;
      const resizeDelta = event.shiftKey ? 0.04 : 0.01;
      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        removePdfSignaturePlacement({ model, placementId });
        setSelectedSignaturePlacementId(null);
        return;
      }
      const patch =
        event.key === 'ArrowLeft'
          ? { rect: { ...rect, x: rect.x - delta } }
          : event.key === 'ArrowRight'
            ? { rect: { ...rect, x: rect.x + delta } }
            : event.key === 'ArrowUp'
              ? { rect: { ...rect, y: rect.y - delta } }
              : event.key === 'ArrowDown'
                ? { rect: { ...rect, y: rect.y + delta } }
                : event.key === '+' || event.key === '='
                  ? {
                      rect: {
                        ...rect,
                        width: rect.width + resizeDelta,
                        height: rect.height + resizeDelta,
                      },
                    }
                  : event.key === '-' || event.key === '_'
                    ? {
                        rect: {
                          ...rect,
                          width: rect.width - resizeDelta,
                          height: rect.height - resizeDelta,
                        },
                      }
                    : null;
      if (!patch) return;
      event.preventDefault();
      setSelectedSignaturePlacementId(placementId);
      updatePdfSignaturePlacement({ model, placementId, patch });
    },
    [model]
  );

  const renderPdfSignatureOverlay = useCallback(
    (pageIndex: number, interactive = true) => (
      <PdfVisualSignatureOverlay
        placements={relevantSignaturePlacements}
        pageIndex={pageIndex}
        appearanceImageUrl={signatures.appearanceImageUrl}
        interactive={interactive}
        selectedPlacementId={selectedSignaturePlacementId}
        removeLabel={t['com.streamlain.pdf.signature.visual.remove']()}
        onPlacementPointerDown={(event, placement) =>
          startSignaturePlacementDrag(event, placement.id, 'move')
        }
        onPlacementPointerMove={updateSignaturePlacementDrag}
        onPlacementPointerUp={finishSignaturePlacementDrag}
        onPlacementPointerCancel={finishSignaturePlacementDrag}
        onPlacementKeyDown={(event, placement) =>
          onSignaturePlacementKeyDown(event, placement.id, placement.rect)
        }
        onResizePointerDown={(event, placement) =>
          startSignaturePlacementDrag(event, placement.id, 'resize')
        }
        onRemove={placement => {
          removePdfSignaturePlacement({
            model,
            placementId: placement.id,
          });
          setSelectedSignaturePlacementId(null);
        }}
      />
    ),
    [
      finishSignaturePlacementDrag,
      model,
      onSignaturePlacementKeyDown,
      relevantSignaturePlacements,
      selectedSignaturePlacementId,
      signatures,
      startSignaturePlacementDrag,
      t,
      updateSignaturePlacementDrag,
    ]
  );

  const viewerRef = useRef<HTMLDivElement>(null);
  const viewerFrameRef = useRef<HTMLDivElement | null>(null);
  const thumbnailSidebarRef = useRef<HTMLElement | null>(null);
  const thumbnailPanelRef = useRef<HTMLDivElement | null>(null);
  const thumbnailResizeFrameRef = useRef<number | null>(null);
  const isThumbnailResizeActiveRef = useRef(false);
  const pendingThumbnailResizeViewportRef = useRef<{
    width: number;
    height: number;
  } | null>(null);
  const thumbnailOverlayModeRef = useRef(false);
  const pdfSelectionStartRef = useRef<PdfGlyphPointer | null>(null);
  const zoomRef = useRef(zoom);
  const cursorRef = useRef(cursor);
  usePdfViewContext({
    attachmentId: source.id,
    name: source.name,
    pageCount: meta.pageCount,
    viewerRef,
    cursorRef,
  });
  const programmaticPageSelectTargetRef = useRef<number | null>(null);
  const gestureZoomStartRef = useRef<number | null>(null);
  const zoomScrollAnchorRef = useRef<PdfZoomScrollAnchor | null>(null);
  // Gesture-preview state: pending zoom accumulates across wheel/pinch ticks
  // while only a CSS scale preview is shown; commit happens debounced.
  const pendingZoomRef = useRef(initialFullscreenTarget.zoom);
  const zoomPreviewBaseRef = useRef<number | null>(null);
  const zoomPreviewAnchorRef = useRef<PdfZoomScrollAnchor | null>(null);
  const zoomPreviewFramesRef = useRef<HTMLElement[]>([]);
  const zoomCommitTimerRef = useRef<number | null>(null);
  const zoomPreviewFrameRef = useRef<number | null>(null);
  const shouldClearPdfSelectionOnClickRef = useRef(false);
  const suppressNextViewerClickClearRef = useRef(false);
  const findRequestSeqRef = useRef(0);
  const pagesScrollerRef = useRef<HTMLElement | null>(null);
  const pagesScrollerHandleRef = useRef<VirtuosoHandle>(null);
  const thumbnailsScrollerHandleRef = useRef<VirtuosoHandle>(null);
  const appliedNavigationKeyRef = useRef<string | null>(null);
  const initialPositionDecidedRef = useRef(false);
  const isApplyingFullscreenRestoreRef = useRef(false);
  const fullscreenSaveTimerRef = useRef<number | null>(null);
  const fullscreenRestoreFrameRef = useRef<number | null>(null);
  const consumedRequestIdRef = useRef<string | null>(null);
  const isStaleNavigationRef = useRef(false);
  const initialPositionAppliedRef = useRef(false);
  const lastFindScrollNonceRef = useRef(0);
  const fullscreenStorageKey = useMemo(
    () => createPdfFullscreenStorageKey(source),
    [source]
  );
  const signaturePlacementDragRef = useRef<PdfSignaturePlacementDrag | null>(
    null
  );

  useLayoutEffect(() => {
    const frame = viewerFrameRef.current;
    if (!frame) return;

    const updateOverlayMode = (width: number) => {
      const nextOverlay = width > 0 && width < PDF_THUMBNAIL_OVERLAY_BREAKPOINT;
      setThumbnailOverlayMode(current =>
        current === nextOverlay ? current : nextOverlay
      );
      if (nextOverlay && !thumbnailOverlayModeRef.current) {
        setCollapsed(true);
      }
      thumbnailOverlayModeRef.current = nextOverlay;
    };

    updateOverlayMode(frame.getBoundingClientRect().width);
    return observeResize(frame, ({ contentRect: { width } }) =>
      updateOverlayMode(width)
    );
  }, []);

  useEffect(() => {
    if (!model || !source.supportsBlockAnnotations) return;
    const entityRef = commentManager.get(model.store.id);
    setCommentEntity(entityRef.obj);
    entityRef.obj.revalidate();
    return () => {
      entityRef.release();
    };
  }, [commentManager, model, source.supportsBlockAnnotations]);

  useEffect(() => {
    zoomRef.current = zoom;
    window.dispatchEvent(
      new CustomEvent('streamlain:pdf-zoom-changed', {
        detail: { blockId: pdfEventId, zoom },
      })
    );
  }, [pdfEventId, zoom]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('streamlain:pdf-layout-changed', {
        detail: {
          blockId: pdfEventId,
          sidebarWidth: sidebarLayoutWidth,
        },
      })
    );
  }, [pdfEventId, sidebarLayoutWidth]);

  useEffect(() => {
    cursorRef.current = cursor;
  }, [cursor]);

  useEffect(() => {
    const thumbnailSidebar = thumbnailSidebarRef.current;
    return () => {
      if (thumbnailResizeFrameRef.current !== null) {
        window.cancelAnimationFrame(thumbnailResizeFrameRef.current);
        thumbnailResizeFrameRef.current = null;
      }
      isThumbnailResizeActiveRef.current = false;
      pendingThumbnailResizeViewportRef.current = null;
      thumbnailSidebar?.removeAttribute('data-resizing');
    };
  }, []);

  const persistFullscreenViewportState = useCallback(() => {
    if (isApplyingFullscreenRestoreRef.current) return;
    if (!initialPositionAppliedRef.current) return;
    const el = pagesScrollerRef.current;
    if (!el || meta.pageCount <= 0) return;
    // The current viewport — including a page reached via a search/cmdk
    // navigation — is the last-opened position and becomes the persisted resume
    // target, so reopening lands exactly there. The search term itself is
    // one-shot: `consumedRequestId` marks the navigation consumed so the find
    // bar does not reopen, while the page is still restored.
    writePdfFullscreenViewportState({
      key: fullscreenStorageKey,
      state: {
        pageIndex: Math.max(0, Math.min(cursorRef.current, meta.pageCount - 1)),
        scrollLeft: Math.max(0, el.scrollLeft),
        scrollTop: Math.max(0, el.scrollTop),
        updatedAt: Date.now(),
        zoom: zoomRef.current,
        consumedRequestId: consumedRequestIdRef.current ?? undefined,
      },
    });
  }, [fullscreenStorageKey, meta.pageCount]);

  const schedulePersistFullscreenViewportState = useCallback(() => {
    if (isApplyingFullscreenRestoreRef.current) return;
    if (!initialPositionAppliedRef.current) return;
    if (fullscreenSaveTimerRef.current !== null) {
      window.clearTimeout(fullscreenSaveTimerRef.current);
    }
    fullscreenSaveTimerRef.current = window.setTimeout(() => {
      fullscreenSaveTimerRef.current = null;
      persistFullscreenViewportState();
    }, 180);
  }, [persistFullscreenViewportState]);

  const updateScrollerRef = useCallback(
    (scroller: HTMLElement | Window | null) => {
      pagesScrollerRef.current = scroller as HTMLElement;
    },
    []
  );

  const onScroll = useCallback(() => {
    const el = pagesScrollerRef.current;
    if (!el) return;

    const { pageCount } = meta;
    if (!pageCount) return;

    const cursor = calculatePageNum(el, pageCount);
    const programmaticTarget = programmaticPageSelectTargetRef.current;
    if (programmaticTarget !== null && cursor !== programmaticTarget) {
      return;
    }
    if (programmaticTarget !== null) {
      programmaticPageSelectTargetRef.current = null;
    }

    if (cursor !== cursorRef.current) {
      cursorRef.current = cursor;
      setCursor(cursor);
    }
    schedulePersistFullscreenViewportState();
  }, [pagesScrollerRef, meta, schedulePersistFullscreenViewportState]);

  const onPageSelect = useCallback(
    (index: number, behavior: 'smooth' | 'auto' = 'smooth') => {
      const scroller = pagesScrollerHandleRef.current;
      if (!scroller) return;
      if (meta.pageCount <= 0) return;
      const targetIndex = Math.max(0, Math.min(index, meta.pageCount - 1));

      // Programmatic navigation owns the viewport now — a pending zoom-anchor
      // re-assert must not yank it back.
      zoomScrollAnchorRef.current = null;
      if (targetIndex !== cursorRef.current) {
        cursorRef.current = targetIndex;
        setCursor(targetIndex);
      }
      programmaticPageSelectTargetRef.current = targetIndex;
      scroller.scrollToIndex({
        index: targetIndex,
        align: 'center',
        behavior,
      });
      thumbnailsScrollerHandleRef.current?.scrollToIndex({
        index: targetIndex,
        align: 'center',
        behavior: 'smooth',
      });
      if (thumbnailOverlayModeRef.current) {
        setCollapsed(true);
      }
    },
    [meta.pageCount, pagesScrollerHandleRef]
  );

  const indexReviewLocator = useMemo(
    () =>
      createPdfSourceIndexReviewLocator({
        source,
        workspaceId: source.workspaceId ?? workspaceService.workspace.id,
        pageIndex: cursor,
      }),
    [cursor, source, workspaceService.workspace.id]
  );

  const selectReviewPage = useCallback(
    (pageIndex: number) => {
      onPageSelect(pageIndex, 'smooth');
    },
    [onPageSelect]
  );

  const activeFindHit = getActivePdfFindHit(findState);

  const findCountLabel = formatPdfFindCount(findState);
  const findOccurrenceLabel =
    findCountLabel === '...' || findCountLabel === '!'
      ? findCountLabel
      : `${findCountLabel} ${t['com.streamlain.pdf.find.occurrences']()}`;
  const findNoticeLabel = useMemo(() => {
    return formatPdfFindNoticeLabel(
      { coverage: findState.coverage, notice: findState.notice },
      {
        capReached: input =>
          t['com.streamlain.pdf.find.notice.capReached'](input),
        localOnly: () => t['com.streamlain.pdf.find.notice.localOnly'](),
        ocrUnavailableOffline: () =>
          t['com.streamlain.pdf.find.notice.ocrUnavailableOffline'](),
        partial: input => t['com.streamlain.pdf.find.notice.partial'](input),
        remoteUnavailable: () =>
          t['com.streamlain.pdf.find.notice.remoteUnavailable'](),
      }
    );
  }, [findState.coverage, findState.notice, t]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('streamlain:pdf-find-state-changed', {
        detail: {
          blockId: pdfEventId,
          open: findState.open,
          query: findState.query,
          countLabel: findOccurrenceLabel,
          noticeLabel: findNoticeLabel,
        },
      })
    );
  }, [
    findNoticeLabel,
    findOccurrenceLabel,
    findState.open,
    findState.query,
    pdfEventId,
  ]);

  const clearPdfSelection = useCallback(() => {
    pdfSelectionStartRef.current = null;
    window.getSelection()?.removeAllRanges();
    setPdfSelection(null);
    setSelectedPdfHighlight(null);
  }, []);

  const onPdfHighlightClick = useCallback(
    (
      rect: PDFPageHighlightRect,
      position: {
        left: number;
        top: number;
      }
    ) => {
      if (rect.commentId) {
        commentPanelService.openCommentPanel();
        commentEntity?.highlightComment(rect.commentId);
        clearPdfSelection();
        return;
      }
      if (rect.highlightId) {
        const root = viewerRef.current;
        if (!root) return;
        pdfSelectionStartRef.current = null;
        window.getSelection()?.removeAllRanges();
        setPdfSelection(null);
        setSelectedPdfHighlight({
          id: rect.highlightId,
          toolbar: getPdfToolbarPosition(root, position.left, position.top, 48),
        });
      }
    },
    [clearPdfSelection, commentEntity, commentPanelService]
  );

  const hasRelevantPdfAnnotations = useMemo(
    () =>
      storedHighlights.some(
        highlight => highlight.sourceId === pdfSourceId && !highlight.commentId
      ),
    [pdfSourceId, storedHighlights]
  );

  const hasRelevantPdfComments = useMemo(
    () =>
      comments.some(
        comment =>
          !comment.resolved &&
          comment.targetAnchors.some(
            anchor =>
              anchor.type === 'pdf-text' &&
              anchor.blockId === source.id &&
              anchor.sourceId === pdfSourceId
          )
      ),
    [comments, pdfSourceId, source.id]
  );

  useEffect(() => {
    if (
      includeAnnotationsInDigitalSignature &&
      !hasRelevantPdfAnnotations &&
      !hasRelevantPdfComments
    ) {
      setIncludeAnnotationsInDigitalSignature(false);
    }
  }, [
    hasRelevantPdfAnnotations,
    hasRelevantPdfComments,
    includeAnnotationsInDigitalSignature,
  ]);

  const getPdfFrameExportOptions = useCallback(
    (
      { includeAnnotations, includeComments }: PdfMarkupExportOptions,
      placementSnapshot = relevantSignaturePlacements
    ) => ({
      pdf,
      meta,
      blockId: source.id,
      sourceId: pdfSourceId,
      fileName: source.name,
      comments,
      highlights: storedHighlights,
      signaturePlacements: placementSnapshot.flatMap(placement =>
        placement.signatureAppearanceId
          ? [
              {
                id: placement.id,
                sourceId: placement.sourceId,
                pageIndex: placement.pageIndex,
                rect: placement.rect,
                rotation: placement.rotation,
                imageUrl: signatures.appearanceImageUrl(
                  placement.signatureAppearanceId
                ),
              },
            ]
          : []
      ),
      includeAnnotations,
      includeComments,
      resolveUserAvatarUrl: async ({
        avatarUrl,
        userId,
      }: {
        avatarUrl: string;
        userId: string;
      }) => {
        const sourceUrl = toAbsoluteAvatarAssetUrl(
          avatarUrl,
          serverService.server.baseUrl
        );
        if (!account || !sourceUrl) {
          return null;
        }
        const input: AvatarAssetCacheInput = {
          kind: 'user-avatar',
          scope: {
            accountId: account.id,
            serverId: serverService.server.id,
          },
          entityId: userId,
          source: sourceUrl,
        };
        return avatarCache.objectUrl(input, serverService.server.baseUrl);
      },
    }),
    [
      account,
      avatarCache,
      comments,
      meta,
      source.id,
      source.name,
      pdf,
      pdfSourceId,
      storedHighlights,
      relevantSignaturePlacements,
      serverService.server.baseUrl,
      serverService.server.id,
      signatures,
    ]
  );

  const createDigitalSigningSnapshot = useCallback(
    async (placementSnapshot = relevantSignaturePlacements) => {
      const bytes = await buildAnnotatedPdfBytes(
        getPdfFrameExportOptions(
          {
            includeAnnotations: true,
            includeComments: true,
          },
          placementSnapshot
        )
      );
      const baseName = (source.name || 'document.pdf').replace(/\.pdf$/i, '');
      const file = new File(
        [new Uint8Array(bytes)],
        `${baseName}-signed-source.pdf`,
        {
          type: 'application/pdf',
        }
      );
      const uploaded = await uploadWorkspaceBlob(
        workspaceService.workspace.id,
        file,
        {
          docId: source.docId,
          context: workspaceService.workspace.blobContext,
        }
      );
      return uploaded.blobId;
    },
    [
      getPdfFrameExportOptions,
      relevantSignaturePlacements,
      source.docId,
      source.name,
      workspaceService.workspace.blobContext,
      workspaceService.workspace.id,
    ]
  );

  const startDigitalSigning = useCallback(async () => {
    // Only a profile that can sign through a usable connection starts a job.
    if (!pdfSourceId || !selectedSigningProfile) return;
    const placement =
      selectedSignaturePlacement ??
      currentSignaturePlacement ??
      (selectedSignatureAppearanceId
        ? upsertPdfSignaturePlacement({
            model,
            appearanceId: selectedSignatureAppearanceId,
            pageIndex: cursorRef.current,
          })
        : null);
    try {
      if (placement) {
        setSelectedSignaturePlacementId(placement.id);
      }
      const shouldIncludeAnnotations =
        includeAnnotationsInDigitalSignature &&
        (hasRelevantPdfAnnotations || hasRelevantPdfComments);
      let signingSourceBlobKey: string | null = null;
      let signingSourceSnapshotId: string | null = null;
      if (shouldIncludeAnnotations) {
        setIsPreparingSigningSnapshot(true);
        const snapshotPlacements = placement
          ? [
              ...relevantSignaturePlacements.filter(
                existing => existing.id !== placement.id
              ),
              placement,
            ]
          : relevantSignaturePlacements;
        signingSourceBlobKey =
          await createDigitalSigningSnapshot(snapshotPlacements);
        const snapshot = await signatures.createSigningSourceSnapshot({
          sourceBlobKey: pdfSourceId,
          signingSourceBlobKey,
          docId: source.docId,
          attachmentBlockId: source.id,
        });
        signingSourceSnapshotId = snapshot.id;
      }
      const job = await signatures.requestSigning({
        sourceBlobKey: pdfSourceId,
        signingSourceBlobKey,
        signingSourceSnapshotId,
        includeAnnotations: shouldIncludeAnnotations,
        docId: source.docId,
        attachmentBlockId: source.id,
        appearanceAssetId: shouldIncludeAnnotations
          ? null
          : selectedSignatureAppearanceId || null,
        placementSnapshot: shouldIncludeAnnotations ? null : placement,
        profileId: selectedSigningProfile.id,
        fileName: source.name,
      });
      // The same file signed again with the same profile gives the copy that
      // exists already: say that, not that a new one was made.
      if (job.status === 'completed') {
        announcedSigningJobIdRef.current = job.id;
        notify({
          title: t['com.streamlain.pdf.signature.toast.signedCopyExists'](),
        });
      }
      setSigningJob(job);
    } catch (error) {
      notifyPdfSignatureError(
        error,
        'sign',
        canOpenWorkspacePlans ? openWorkspacePlans : null
      );
    } finally {
      setIsPreparingSigningSnapshot(false);
    }
  }, [
    canOpenWorkspacePlans,
    createDigitalSigningSnapshot,
    currentSignaturePlacement,
    hasRelevantPdfAnnotations,
    hasRelevantPdfComments,
    includeAnnotationsInDigitalSignature,
    model,
    pdfSourceId,
    relevantSignaturePlacements,
    selectedSignatureAppearanceId,
    selectedSignaturePlacement,
    selectedSigningProfile,
    openWorkspacePlans,
    signatures,
    source.docId,
    source.id,
    source.name,
    t,
  ]);

  const placeSignedCopyOnCanvas = useCallback(
    async (version: SignedPdfVersionInfo) => {
      if (!model) return;
      setPlacingSignedVersionId(version.id);
      try {
        const file = await fetchSignedPdfVersionFile({
          fileName: version.fileName || source.name || 'signed.pdf',
          url: signatures.signedVersionDownloadUrl(version.id),
          workspaceId: workspaceService.workspace.id,
        });
        const placement = await insertSignedPdfCopyBesideSource({
          docId: source.docId,
          file,
          signedVersion: version,
          sourceModel: model,
          context: workspaceService.workspace.blobContext,
          workspaceId: workspaceService.workspace.id,
        });
        if (std) {
          focusSignedPdfCopyAttachment({
            blockId: placement.blockId,
            std,
            xywh: placement.xywh,
          });
        }
        notify.success({
          title: getSignedPdfCopyPlacedLabel(),
        });
        onClose?.();
      } catch (error) {
        notifyPdfSignatureError(error, 'export');
      } finally {
        setPlacingSignedVersionId(null);
      }
    },
    [
      model,
      onClose,
      signatures,
      source.docId,
      source.name,
      std,
      workspaceService.workspace.blobContext,
      workspaceService.workspace.id,
    ]
  );

  const askPdfPrintOptions = useCallback(
    () =>
      new Promise<PdfMarkupExportOptions>(resolve => {
        const initialOptions = {
          includeAnnotations: false,
          includeComments: false,
        };
        if (!hasRelevantPdfAnnotations && !hasRelevantPdfComments) {
          resolve(initialOptions);
          return;
        }

        let settled = false;
        let selectedOptions = initialOptions;
        const finish = (value: PdfMarkupExportOptions) => {
          if (settled) return;
          settled = true;
          resolve(value);
        };

        openConfirmModal({
          title: t['com.streamlain.pdf.print.options.title'](),
          description: t['com.streamlain.pdf.print.options.description'](),
          confirmText: t['com.streamlain.pdf.print.options.confirm'](),
          cancelText: t['com.streamlain.pdf.print.options.cancel'](),
          children: (
            <PdfPrintOptionsContent
              hasAnnotations={hasRelevantPdfAnnotations}
              hasComments={hasRelevantPdfComments}
              initialOptions={initialOptions}
              onChange={options => {
                selectedOptions = options;
              }}
            />
          ),
          onOpenChange: open => {
            if (!open) {
              finish({ includeAnnotations: false, includeComments: false });
            }
          },
          onCancel: () =>
            finish({ includeAnnotations: false, includeComments: false }),
          onConfirm: () => finish(selectedOptions),
        });
      }),
    [hasRelevantPdfAnnotations, hasRelevantPdfComments, openConfirmModal, t]
  );

  const recordVisualSignatureExport = useCallback(
    async (output: 'print' | 'export') => {
      if (!pdfSourceId || relevantSignaturePlacements.length === 0) return;
      await signatures.recordVisualExport({
        sourceBlobKey: pdfSourceId,
        docId: source.docId,
        attachmentBlockId: source.id,
        placementCount: relevantSignaturePlacements.length,
        output,
      });
    },
    [
      pdfSourceId,
      relevantSignaturePlacements.length,
      signatures,
      source.docId,
      source.id,
    ]
  );

  // A workspace can demand that a PDF with inserted signatures leaves only as
  // a digitally signed copy. The viewer says so before it prints or downloads.
  const visualExportBlocked =
    relevantSignaturePlacements.length > 0 &&
    signatures.policy?.allowVisualOnlyExport === false;
  const refuseVisualExport = useCallback(() => {
    notify.error({
      title: t['com.streamlain.pdf.signature.policy.signedCopyOnly.title'](),
      message:
        t['com.streamlain.pdf.signature.policy.signedCopyOnly.message'](),
    });
  }, [t]);

  const handlePrintPdfFrames = useCallback(() => {
    if (visualExportBlocked) {
      refuseVisualExport();
      return;
    }
    void askPdfPrintOptions()
      .then(async options => {
        await recordVisualSignatureExport('print');
        await printPdfFrames(getPdfFrameExportOptions(options));
      })
      .catch(error => {
        console.error('Failed to print PDF frames', error);
        notifyPdfSignatureError(error, 'export');
      });
  }, [
    askPdfPrintOptions,
    getPdfFrameExportOptions,
    recordVisualSignatureExport,
    refuseVisualExport,
    visualExportBlocked,
  ]);

  // The PDF as it leaves the viewer, for a download or a mail: the original,
  // or with its inserted signatures drawn in. Null when nothing leaves: the
  // workspace only lets it out as a digitally signed copy (the viewer has said
  // so), or the original is not at hand.
  const buildPdfForExport = useCallback(async () => {
    const name = resolveBlobDownloadName({
      blobId: source.blobId,
      occurrenceName: source.name,
      mimeType: source.mimeType,
    });
    if (relevantSignaturePlacements.length === 0) {
      const blob = await getAttachmentBlob(source);
      return blob ? { blob, name, withSignatures: false } : null;
    }
    if (visualExportBlocked) {
      refuseVisualExport();
      return null;
    }
    const refuseTooLarge = () => {
      notify.error({
        title: t['com.streamlain.pdf.signature.tooLargeToEdit.title'](),
        message: t['com.streamlain.pdf.signature.tooLargeToEdit.message'](),
      });
      return null;
    };
    // A known size is checked before any byte is loaded.
    if (source.size !== null && !canInsertVisualSignatures(source.size)) {
      return refuseTooLarge();
    }
    const sourcePdf = await getAttachmentBlob(source);
    if (!sourcePdf) {
      throw new Error('Could not load PDF source for export');
    }
    if (!canInsertVisualSignatures(sourcePdf.size)) {
      return refuseTooLarge();
    }
    const signaturePlacements = getPdfFrameExportOptions({
      includeAnnotations: false,
      includeComments: false,
    }).signaturePlacements;
    const bytes = await buildPdfWithVisualSignatures({
      sourcePdf,
      signaturePlacements,
    });
    return {
      blob: new Blob([new Uint8Array(bytes)], { type: 'application/pdf' }),
      name,
      withSignatures: true,
    };
  }, [
    getPdfFrameExportOptions,
    refuseVisualExport,
    relevantSignaturePlacements.length,
    source,
    t,
    visualExportBlocked,
  ]);

  const recordPdfExport = useCallback(() => {
    void recordVisualSignatureExport('export').catch(error => {
      console.error('Failed to record visual PDF signature export', error);
    });
  }, [recordVisualSignatureExport]);

  const handleDownloadPdf = useCallback(async () => {
    const pdf = await buildPdfForExport();
    if (!pdf) return;
    await downloadBlob(pdf.blob, pdf.name);
    if (pdf.withSignatures) recordPdfExport();
  }, [buildPdfForExport, recordPdfExport]);

  // Send by mail: the reader closes and the mail view opens on a new message
  // that carries the PDF as a file, like one picked in the composer.
  const handleSendPdfAsMail = useCallback(async () => {
    if (!canSendPdfAsMail) return;
    const pdf = await buildPdfForExport();
    if (!pdf) return;
    mailComposeHandoff.requestNewMessage({
      files: [new File([pdf.blob], pdf.name, { type: 'application/pdf' })],
    });
    if (pdf.withSignatures) recordPdfExport();
    workbenchService.workbench.open('/mail');
    onClose?.();
  }, [
    buildPdfForExport,
    canSendPdfAsMail,
    mailComposeHandoff,
    onClose,
    recordPdfExport,
    workbenchService,
  ]);

  useEffect(() => {
    const onSendMail = (event: Event) => {
      const detail = (event as CustomEvent<{ blockId?: string }>).detail;
      if (detail?.blockId && detail.blockId !== pdfEventId) return;
      event.stopImmediatePropagation();
      void handleSendPdfAsMail().catch(error => {
        console.error('Failed to hand the PDF to a new mail', error);
        notifyPdfSignatureError(error, 'export');
      });
    };
    window.addEventListener(PDF_SEND_MAIL_EVENT, onSendMail);
    return () => window.removeEventListener(PDF_SEND_MAIL_EVENT, onSendMail);
  }, [handleSendPdfAsMail, pdfEventId]);

  // The reader lies over the doc and its sidebar: while it is open, it is the
  // place for the doc's comments.
  useEffect(() => {
    if (!commentEntity) return;
    return commentPanelService.claimSurface({
      open: () => setCommentsOpen(true),
    });
  }, [commentEntity, commentPanelService]);

  const closeComments = useCallback(() => {
    commentEntity?.dismissDraftComment();
    setCommentsOpen(false);
  }, [commentEntity]);

  useEffect(() => {
    const onToggle = (event: Event) => {
      const detail = (event as CustomEvent<{ blockId?: string }>).detail;
      if (detail?.blockId && detail.blockId !== pdfEventId) return;
      if (commentsOpen) closeComments();
      else setCommentsOpen(true);
    };
    window.addEventListener(PDF_COMMENTS_TOGGLE_EVENT, onToggle);
    return () =>
      window.removeEventListener(PDF_COMMENTS_TOGGLE_EVENT, onToggle);
  }, [closeComments, commentsOpen, pdfEventId]);

  // The titlebar offers the comments only where the viewer carries them.
  const commentsAvailable = commentEntity !== null;
  useEffect(() => {
    const dispatchState = () => {
      window.dispatchEvent(
        new CustomEvent(PDF_COMMENTS_STATE_EVENT, {
          detail: {
            blockId: pdfEventId,
            available: commentsAvailable,
            open: commentsOpen,
          },
        })
      );
    };
    dispatchState();
    const frame = window.requestAnimationFrame(dispatchState);
    return () => window.cancelAnimationFrame(frame);
  }, [commentsAvailable, commentsOpen, pdfEventId]);

  // The titlebar offers "send by mail" only where the viewer can do it.
  useEffect(() => {
    const dispatchState = () => {
      window.dispatchEvent(
        new CustomEvent(PDF_MAIL_STATE_EVENT, {
          detail: { blockId: pdfEventId, available: canSendPdfAsMail },
        })
      );
    };
    dispatchState();
    const frame = window.requestAnimationFrame(dispatchState);
    return () => window.cancelAnimationFrame(frame);
  }, [canSendPdfAsMail, pdfEventId]);

  useEffect(() => {
    const onDownload = (event: Event) => {
      const detail = (event as CustomEvent<{ blockId?: string }>).detail;
      if (detail?.blockId && detail.blockId !== pdfEventId) return;
      event.stopImmediatePropagation();
      void handleDownloadPdf().catch(error => {
        console.error('Failed to download PDF with visual signatures', error);
        notifyPdfSignatureError(error, 'export');
      });
    };
    window.addEventListener(PDF_DOWNLOAD_EVENT, onDownload);
    return () => window.removeEventListener(PDF_DOWNLOAD_EVENT, onDownload);
  }, [handleDownloadPdf, pdfEventId]);

  useEffect(() => {
    const onPrint = (event: Event) => {
      const detail = (event as CustomEvent<{ blockId?: string }>).detail;
      if (detail?.blockId && detail.blockId !== pdfEventId) return;
      handlePrintPdfFrames();
    };

    window.addEventListener(PDF_PRINT_EVENT, onPrint);
    return () => {
      window.removeEventListener(PDF_PRINT_EVENT, onPrint);
    };
  }, [handlePrintPdfFrames, pdfEventId]);

  // Viewport available to the main pages list (shared by the Virtuoso context
  // and the zoom-anchor target-size computation).
  const pagesViewportInfo = useMemo(
    () => ({
      width: Math.max(240, viewportInfo.width - 40),
      height: viewportInfo.height - 40,
    }),
    [viewportInfo.width, viewportInfo.height]
  );

  // Target for the gesture-time CSS preview: only mounted page frames, not the
  // whole virtualized scroll content. The scroll content can be many thousands
  // of pixels tall; promoting that as one transformed layer ruckelt on trackpad
  // pinch. Mounted page frames stay small, preserve zoom-out overfill, and avoid
  // React/PDFium work per wheel tick.
  const getZoomPreviewFrames = useCallback(() => {
    const el = pagesScrollerRef.current;
    if (!el) return [];
    return Array.from(
      el.querySelectorAll<HTMLElement>('[data-pdf-page-frame-index]')
    );
  }, []);

  const clearZoomPreviewStyles = useCallback(() => {
    for (const frame of zoomPreviewFramesRef.current) {
      frame.style.transform = '';
      frame.style.transformOrigin = '';
      frame.style.willChange = '';
      frame.style.zIndex = '';
    }
    zoomPreviewFramesRef.current = [];
  }, []);

  // Captured once per zoom gesture (not per wheel tick): the content point the
  // zoom is anchored to. Page-relative when a page is under the anchor point,
  // viewport-ratio otherwise (cursor over the gap/background).
  const capturePdfZoomAnchor = useCallback(
    (el: HTMLElement, anchor?: PdfZoomAnchor): PdfZoomScrollAnchor => {
      const rect = el.getBoundingClientRect();
      const localX =
        typeof anchor?.clientX === 'number'
          ? Math.min(Math.max(anchor.clientX - rect.left, 0), el.clientWidth)
          : el.clientWidth / 2;
      const localY =
        typeof anchor?.clientY === 'number'
          ? Math.min(Math.max(anchor.clientY - rect.top, 0), el.clientHeight)
          : el.clientHeight / 2;
      const anchorClientX = rect.left + localX;
      const anchorClientY = rect.top + localY;
      const pageLayer = getPdfPageFrameAtPoint({
        root: el,
        clientX: anchorClientX,
        clientY: anchorClientY,
      });
      const pageIndex = Number(pageLayer?.dataset.pdfPageFrameIndex);
      if (pageLayer && Number.isFinite(pageIndex)) {
        const pageBounds = pageLayer.getBoundingClientRect();
        const pageWidth = Math.max(1, pageBounds.width);
        const pageHeight = Math.max(1, pageBounds.height);
        return {
          type: 'page',
          pageIndex,
          pageX: clampUnit((anchorClientX - pageBounds.left) / pageWidth),
          pageY: clampUnit((anchorClientY - pageBounds.top) / pageHeight),
          clientX: anchorClientX,
          clientY: anchorClientY,
        };
      }
      return {
        type: 'viewport',
        localX,
        localY,
        ratioX: (el.scrollLeft + localX) / Math.max(1, el.scrollWidth),
        ratioY: (el.scrollTop + localY) / Math.max(1, el.scrollHeight),
      };
    },
    []
  );

  // Re-apply the scroll correction for the captured zoom anchor. Idempotent and
  // measurement-based: runs in the zoom layout effect AND again on every
  // Virtuoso total-height change, because Virtuoso re-measures the resized
  // pages asynchronously (ResizeObserver) and shifts the content afterwards —
  // a single layout-effect correction gets clobbered by that re-measure.
  // Clears itself once the anchor point is stable under the cursor.
  const applyZoomScrollAnchor = useCallback(() => {
    const anchor = zoomScrollAnchorRef.current;
    const el = pagesScrollerRef.current;
    if (!anchor || !el) return;

    if (anchor.type === 'page') {
      const pageLayer = el.querySelector<HTMLElement>(
        `[data-pdf-page-frame-index="${anchor.pageIndex}"]`
      );
      const pageBounds = pageLayer?.getBoundingClientRect();
      if (!pageBounds) {
        // Page not mounted (yet) at the current scroll position — keep the
        // anchor pending; the next total-height change retries.
        return;
      }
      const sizeReady =
        typeof anchor.expectedPageWidth !== 'number' ||
        typeof anchor.expectedPageHeight !== 'number' ||
        (Math.abs(pageBounds.width - anchor.expectedPageWidth) <= 2 &&
          Math.abs(pageBounds.height - anchor.expectedPageHeight) <= 2);
      if (!sizeReady) {
        // Virtuoso has not re-rendered the anchor page at the committed zoom
        // yet — measuring now would see the old layout and report zero drift.
        return;
      }
      const dx =
        pageBounds.left + anchor.pageX * pageBounds.width - anchor.clientX;
      const dy =
        pageBounds.top + anchor.pageY * pageBounds.height - anchor.clientY;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) {
        zoomScrollAnchorRef.current = null;
        return;
      }
      el.scrollLeft = Math.max(0, el.scrollLeft + dx);
      el.scrollTop = Math.max(0, el.scrollTop + dy);
    } else {
      const measured =
        typeof anchor.commitScrollHeight !== 'number' ||
        el.scrollHeight !== anchor.commitScrollHeight;
      el.scrollLeft = Math.max(
        0,
        anchor.ratioX * el.scrollWidth - anchor.localX
      );
      el.scrollTop = Math.max(
        0,
        anchor.ratioY * el.scrollHeight - anchor.localY
      );
      // Once the re-measured total height landed, this apply used the final
      // geometry — done.
      if (measured) {
        zoomScrollAnchorRef.current = null;
      }
    }
  }, []);

  const prepareZoomAnchorForCommit = useCallback(
    (anchor: PdfZoomScrollAnchor | null, next: number) => {
      if (!anchor) return null;
      if (anchor.type === 'page') {
        // Target size the anchor page will have at the committed zoom — the
        // correction in applyZoomScrollAnchor waits for it (Virtuoso updates
        // item content asynchronously after this commit).
        const fit = fitToPage(
          pagesViewportInfo,
          meta.pageSizes[anchor.pageIndex],
          meta.maxSize
        );
        anchor.expectedPageWidth = Math.ceil(fit.width * next);
        anchor.expectedPageHeight = Math.ceil(fit.height * next);
      } else {
        anchor.commitScrollHeight = pagesScrollerRef.current?.scrollHeight;
      }
      return anchor;
    },
    [meta, pagesViewportInfo]
  );

  // Commit the pending preview zoom into React state. The CSS preview transform
  // is cleared in the zoom layout effect, atomically with the new layout, so no
  // frame paints the old layout without the preview scale.
  const commitContinuousZoom = useCallback(() => {
    if (zoomCommitTimerRef.current !== null) {
      window.clearTimeout(zoomCommitTimerRef.current);
      zoomCommitTimerRef.current = null;
    }
    if (zoomPreviewFrameRef.current !== null) {
      window.cancelAnimationFrame(zoomPreviewFrameRef.current);
      zoomPreviewFrameRef.current = null;
    }
    if (zoomPreviewBaseRef.current === null) return;
    zoomPreviewBaseRef.current = null;
    const next = pendingZoomRef.current;
    const anchor = zoomPreviewAnchorRef.current;
    zoomPreviewAnchorRef.current = null;
    if (next === zoomRef.current) {
      clearZoomPreviewStyles();
      return;
    }
    zoomScrollAnchorRef.current = prepareZoomAnchorForCommit(anchor, next);
    zoomRef.current = next;
    setZoom(next);
  }, [clearZoomPreviewStyles, prepareZoomAnchorForCommit]);

  // Discrete zoom = toolbar/menu command. It commits directly through React and
  // reuses the same scroll-anchor correction as gesture zoom, but intentionally
  // skips the compositor preview transform that is only valid for live gestures.
  const setDiscreteZoom = useCallback(
    (nextZoom: number, anchor?: PdfZoomAnchor) => {
      const next = Math.round(clampPdfZoom(nextZoom) * 1000) / 1000;
      const el = pagesScrollerRef.current;

      if (zoomCommitTimerRef.current !== null) {
        window.clearTimeout(zoomCommitTimerRef.current);
        zoomCommitTimerRef.current = null;
      }
      if (zoomPreviewFrameRef.current !== null) {
        window.cancelAnimationFrame(zoomPreviewFrameRef.current);
        zoomPreviewFrameRef.current = null;
      }
      zoomPreviewBaseRef.current = null;
      zoomPreviewAnchorRef.current = null;
      clearZoomPreviewStyles();

      if (next === zoomRef.current) {
        pendingZoomRef.current = zoomRef.current;
        return;
      }

      if (!el || isApplyingFullscreenRestoreRef.current) {
        pendingZoomRef.current = next;
        zoomRef.current = next;
        setZoom(next);
        return;
      }

      const captured = capturePdfZoomAnchor(el, anchor);
      zoomScrollAnchorRef.current = prepareZoomAnchorForCommit(captured, next);
      pendingZoomRef.current = next;
      zoomRef.current = next;
      setZoom(next);
    },
    [capturePdfZoomAnchor, clearZoomPreviewStyles, prepareZoomAnchorForCommit]
  );

  // Continuous zoom = gesture preview + debounced commit. Wheel/pinch ticks
  // only mutate compositor-side CSS scale on the mounted page frames (no React
  // render, no PDFium render); the real zoom commits once, 160ms after the
  // last tick. Committing per tick re-reconciled every mounted page including
  // thousands of text-layer glyph spans and re-rendered them through PDFium.
  const setContinuousZoom = useCallback(
    (nextZoom: number, anchor?: PdfZoomAnchor) => {
      const next = Math.round(clampPdfZoom(nextZoom) * 1000) / 1000;
      const el = pagesScrollerRef.current;

      if (!el || isApplyingFullscreenRestoreRef.current) {
        if (next === zoomRef.current) return;
        pendingZoomRef.current = next;
        zoomRef.current = next;
        setZoom(next);
        return;
      }

      if (zoomPreviewBaseRef.current === null) {
        if (next === zoomRef.current) return;
        zoomPreviewBaseRef.current = zoomRef.current;
        const captured = capturePdfZoomAnchor(el, anchor);
        zoomPreviewAnchorRef.current = captured;
        const rect = el.getBoundingClientRect();
        const originClientX =
          captured.type === 'page'
            ? captured.clientX
            : rect.left + captured.localX;
        const originClientY =
          captured.type === 'page'
            ? captured.clientY
            : rect.top + captured.localY;
        const frames = getZoomPreviewFrames();
        zoomPreviewFramesRef.current = frames;
        for (const frame of frames) {
          const frameRect = frame.getBoundingClientRect();
          frame.style.willChange = 'transform';
          frame.style.transformOrigin = `${originClientX - frameRect.left}px ${
            originClientY - frameRect.top
          }px`;
          frame.style.zIndex = '1';
        }
      }

      pendingZoomRef.current = next;
      // Batch the visual update per frame: wheel events can arrive faster than
      // the display refresh (trackpad pinch ~120Hz) and the handler runs with
      // passive:false, so per-event style writes + titlebar event dispatches
      // would stall the input pipeline.
      if (zoomPreviewFrameRef.current === null) {
        zoomPreviewFrameRef.current = window.requestAnimationFrame(() => {
          zoomPreviewFrameRef.current = null;
          const base = zoomPreviewBaseRef.current;
          if (base === null) return;
          const scale = pendingZoomRef.current / base;
          for (const frame of zoomPreviewFramesRef.current) {
            frame.style.transform = `translateZ(0) scale(${scale})`;
          }
          // Live zoom readout for the titlebar while the gesture is active.
          window.dispatchEvent(
            new CustomEvent('streamlain:pdf-zoom-changed', {
              detail: { blockId: pdfEventId, zoom: pendingZoomRef.current },
            })
          );
        });
      }

      if (zoomCommitTimerRef.current !== null) {
        window.clearTimeout(zoomCommitTimerRef.current);
      }
      zoomCommitTimerRef.current = window.setTimeout(() => {
        zoomCommitTimerRef.current = null;
        commitContinuousZoom();
      }, 160);
    },
    [
      capturePdfZoomAnchor,
      commitContinuousZoom,
      getZoomPreviewFrames,
      pdfEventId,
    ]
  );

  useEffect(() => {
    return () => {
      if (zoomCommitTimerRef.current !== null) {
        window.clearTimeout(zoomCommitTimerRef.current);
        zoomCommitTimerRef.current = null;
      }
      if (zoomPreviewFrameRef.current !== null) {
        window.cancelAnimationFrame(zoomPreviewFrameRef.current);
        zoomPreviewFrameRef.current = null;
      }
      if (zoomPreviewBaseRef.current !== null) {
        // Unmount mid-gesture: the pending zoom is the effective zoom for the
        // persisted viewport state (cleanup order: this runs before the
        // persist-on-unmount effect declared further down).
        zoomRef.current = pendingZoomRef.current;
        zoomPreviewBaseRef.current = null;
      }
    };
  }, []);

  useLayoutEffect(() => {
    clearZoomPreviewStyles();
    applyZoomScrollAnchor();
  }, [zoom, applyZoomScrollAnchor, clearZoomPreviewStyles]);

  // Sequential prefetch: keep the next two pages (and roughly one behind)
  // mounted so they render into the bitmap cache before the user reaches
  // them. Margins scale with the committed page height — at high zoom a
  // single page exceeds the viewport, so a fixed viewport-based margin would
  // not even cover the adjacent page.
  const prefetchMargins = useMemo(() => {
    const fallbackTop = Math.round(viewportInfo.height * 0.5);
    const fallbackBottom = viewportInfo.height;
    if (meta.maxSize.width <= 0 || meta.maxSize.height <= 0) {
      return { top: fallbackTop, bottom: fallbackBottom };
    }
    const fit = fitToPage(pagesViewportInfo, meta.maxSize, meta.maxSize);
    const pageHeight = Math.ceil(fit.height * zoom);
    return {
      top: Math.max(fallbackTop, pageHeight),
      bottom: Math.max(fallbackBottom, pageHeight * 2),
    };
  }, [meta.maxSize, pagesViewportInfo, viewportInfo.height, zoom]);

  // Virtuoso reports total-height changes after it re-measured the resized
  // pages; that's the moment its content shift is applied and the anchor
  // correction must be re-asserted.
  const onTotalListHeightChanged = useCallback(() => {
    if (!zoomScrollAnchorRef.current) return;
    applyZoomScrollAnchor();
  }, [applyZoomScrollAnchor]);

  const onThumbnailResizePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const startX = event.clientX;
      const startWidth = thumbnailPanelWidth;
      const target = event.currentTarget;
      const viewerFrame = viewerFrameRef.current;
      const thumbnailSidebar = thumbnailSidebarRef.current;
      const thumbnailPanel = thumbnailPanelRef.current;
      let nextWidth = startWidth;

      target.setPointerCapture(event.pointerId);
      target.setAttribute('data-active', 'true');
      isThumbnailResizeActiveRef.current = true;
      pendingThumbnailResizeViewportRef.current = null;
      thumbnailSidebar?.setAttribute('data-resizing', 'true');

      const applyWidth = () => {
        thumbnailResizeFrameRef.current = null;
        const width = `${nextWidth}px`;
        viewerFrame?.style.setProperty('--pdf-sidebar-width', width);
        viewerFrame?.style.setProperty('--pdf-sidebar-layout-width', width);
        if (thumbnailPanel) {
          thumbnailPanel.style.width = width;
        }
      };

      const scheduleWidth = () => {
        if (thumbnailResizeFrameRef.current !== null) return;
        thumbnailResizeFrameRef.current =
          window.requestAnimationFrame(applyWidth);
      };

      const onPointerMove = (moveEvent: PointerEvent) => {
        nextWidth = clampThumbnailPanelWidth(
          startWidth + moveEvent.clientX - startX
        );
        scheduleWidth();
      };
      const onPointerUp = (upEvent: PointerEvent) => {
        target.removeEventListener('pointermove', onPointerMove);
        target.removeEventListener('pointerup', onPointerUp);
        target.removeEventListener('pointercancel', onPointerUp);
        if (thumbnailResizeFrameRef.current !== null) {
          window.cancelAnimationFrame(thumbnailResizeFrameRef.current);
          thumbnailResizeFrameRef.current = null;
        }
        applyWidth();
        isThumbnailResizeActiveRef.current = false;
        const pendingViewport = pendingThumbnailResizeViewportRef.current;
        pendingThumbnailResizeViewportRef.current = null;
        setThumbnailPanelWidth(nextWidth);
        const viewer = viewerRef.current;
        if (viewer) {
          const { width, height } = viewer.getBoundingClientRect();
          setViewportInfo({ width, height });
        } else if (pendingViewport) {
          setViewportInfo(pendingViewport);
        }
        thumbnailSidebar?.removeAttribute('data-resizing');
        target.removeAttribute('data-active');
        if (target.hasPointerCapture(upEvent.pointerId)) {
          target.releasePointerCapture(upEvent.pointerId);
        }
        window.requestAnimationFrame(() => {
          thumbnailsScrollerHandleRef.current?.scrollToIndex({
            index: cursorRef.current,
            align: 'center',
            behavior: 'auto',
          });
        });
      };

      target.addEventListener('pointermove', onPointerMove);
      target.addEventListener('pointerup', onPointerUp);
      target.addEventListener('pointercancel', onPointerUp);
    },
    [thumbnailPanelWidth]
  );

  const applyPinchZoom = useCallback(
    (factor: number, anchor?: PdfZoomAnchor) => {
      if (!Number.isFinite(factor) || factor <= 0) return;
      // pendingZoomRef is the effective zoom while a gesture preview is active.
      setContinuousZoom(pendingZoomRef.current * factor, anchor);
    },
    [setContinuousZoom]
  );

  const onPdfWheel = useCallback(
    (event: WheelEvent) => {
      programmaticPageSelectTargetRef.current = null;
      if (!event.ctrlKey) {
        // Plain scroll: a pending zoom anchor must not yank the viewport back,
        // and an active preview commits now so layout catches up.
        zoomScrollAnchorRef.current = null;
        commitContinuousZoom();
        return;
      }
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, button, [role="menu"]')) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      applyPinchZoom(Math.exp(-event.deltaY * 0.006), {
        clientX: event.clientX,
        clientY: event.clientY,
      });
    },
    [applyPinchZoom, commitContinuousZoom]
  );

  const onGestureStart = useCallback((event: Event) => {
    event.preventDefault();
    gestureZoomStartRef.current = pendingZoomRef.current;
  }, []);

  const onGestureChange = useCallback(
    (event: Event) => {
      event.preventDefault();
      const gesture = event as Event & {
        clientX?: number;
        clientY?: number;
        scale?: number;
      };
      const scale = gesture.scale;
      if (!scale || !gestureZoomStartRef.current) return;
      const acceleratedScale = 1 + (scale - 1) * 3;
      setContinuousZoom(gestureZoomStartRef.current * acceleratedScale, {
        clientX: gesture.clientX,
        clientY: gesture.clientY,
      });
    },
    [setContinuousZoom]
  );

  const onGestureEnd = useCallback(
    (event: Event) => {
      event.preventDefault();
      gestureZoomStartRef.current = null;
      commitContinuousZoom();
    },
    [commitContinuousZoom]
  );

  const resizePdfPage = useCallback(
    (
      viewportInfo: { width: number; height: number },
      actualSize: { width: number; height: number },
      maxSize: { width: number; height: number },
      isThumbnail?: boolean
    ) => {
      const size = fitToPage(viewportInfo, actualSize, maxSize, isThumbnail);
      if (isThumbnail) return size;
      return {
        ...size,
        width: Math.ceil(size.width * zoom),
        height: Math.ceil(size.height * zoom),
      };
    },
    [zoom]
  );

  const pageCachePrefetchCursor =
    meta.pageCount <= PDF_EAGER_PAGE_CACHE_LIMIT ? 0 : cursor;
  const pageCachePrefetchKey =
    meta.pageCount <= PDF_EAGER_PAGE_CACHE_LIMIT
      ? `all:${meta.pageCount}`
      : `near:${meta.pageCount}:${pageCachePrefetchCursor}`;

  // Pre-render likely next pages into the bitmap cache, independent of
  // viewport/mount state. Small PDFs are cheap enough to warm completely;
  // larger files stay bounded around the cursor. Uses a dedicated pool key so
  // it never interferes with mounted page renderers.
  useEffect(() => {
    if (meta.pageCount <= 0) return;
    const scale = window.devicePixelRatio;
    const disposers: (() => void)[] = [];
    for (const pageNum of getPdfPageCachePrefetchCandidates(
      meta.pageCount,
      pageCachePrefetchCursor
    )) {
      const pageSize = meta.pageSizes[pageNum];
      if (!pageSize) continue;
      const size = resizePdfPage(pagesViewportInfo, pageSize, meta.maxSize);
      if (size.width * size.height === 0) continue;
      const targetWidth = Math.ceil(size.width * scale);
      const cached = readPdfPageBitmapCache(
        pdf.id,
        pageNum,
        'page',
        targetWidth
      );
      if (cached && cached.width >= targetWidth) continue;

      const { page, release } = pdf.page(pageNum, 'prefetch');
      const subscription = page.bitmap$.subscribe(bitmap => {
        if (bitmap) {
          writePdfPageBitmapCache(pdf.id, pageNum, 'page', bitmap);
        }
      });
      page.render({ width: size.width, height: size.height, scale });
      disposers.push(() => {
        subscription.unsubscribe();
        page.render.reset();
        release();
      });
    }
    return () => disposers.forEach(dispose => dispose());
  }, [
    meta,
    pageCachePrefetchCursor,
    pageCachePrefetchKey,
    pagesViewportInfo,
    pdf,
    resizePdfPage,
  ]);

  const deleteSelectedPdfHighlight = useCallback(() => {
    if (!selectedPdfHighlight) return;
    removePdfTextHighlight({
      model,
      highlightId: selectedPdfHighlight.id,
    });
    clearPdfSelection();
  }, [clearPdfSelection, model, selectedPdfHighlight]);

  const onViewerBackgroundClick = useCallback(() => {
    coverage.closeContextMenu();
    if (suppressNextViewerClickClearRef.current) {
      suppressNextViewerClickClearRef.current = false;
      return;
    }
    clearPdfSelection();
  }, [clearPdfSelection, coverage]);

  const goToNextFindHit = useCallback(() => {
    dispatchFind({ type: 'next' });
  }, []);

  const goToPreviousFindHit = useCallback(() => {
    dispatchFind({ type: 'previous' });
  }, []);

  const closeFindBar = useCallback(() => {
    dispatchFind({ type: 'close' });
  }, []);

  const openFindBar = useCallback(() => {
    dispatchFind({ type: 'open' });
  }, []);

  useEffect(() => {
    const onFindOpen = (event: Event) => {
      const detail = (event as CustomEvent<{ blockId?: string }>).detail;
      if (detail?.blockId && detail.blockId !== pdfEventId) return;
      openFindBar();
    };
    const onFindQuery = (event: Event) => {
      const detail = (
        event as CustomEvent<{ blockId?: string; query?: string }>
      ).detail;
      if (detail?.blockId && detail.blockId !== pdfEventId) return;
      dispatchFind({ type: 'query', query: detail?.query ?? '' });
    };
    const onFindAction = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          blockId?: string;
          action?: 'previous' | 'next' | 'close';
        }>
      ).detail;
      if (detail?.blockId && detail.blockId !== pdfEventId) return;
      if (detail?.action === 'previous') {
        goToPreviousFindHit();
      } else if (detail?.action === 'next') {
        goToNextFindHit();
      } else if (detail?.action === 'close') {
        closeFindBar();
      }
    };
    const onZoomSet = (event: Event) => {
      const detail = (event as CustomEvent<{ blockId?: string; zoom?: number }>)
        .detail;
      if (detail?.blockId && detail.blockId !== pdfEventId) return;
      if (typeof detail?.zoom !== 'number') return;
      setDiscreteZoom(detail.zoom);
    };
    const onZoomFit = (event: Event) => {
      const detail = (
        event as CustomEvent<{ blockId?: string; mode?: 'width' | 'height' }>
      ).detail;
      if (detail?.blockId && detail.blockId !== pdfEventId) return;
      if (detail?.mode !== 'width' && detail?.mode !== 'height') return;
      const pageSize = meta.pageSizes[cursorRef.current];
      if (!pageSize) return;
      // Pages lay out at `fitToPage(viewport, page) * zoom`, so the zoom that
      // makes the current page span the viewport axis is the ratio between the
      // viewport axis and the page's base-fit size on that axis.
      const fit = fitToPage(pagesViewportInfo, pageSize, meta.maxSize);
      const target =
        detail.mode === 'width'
          ? pagesViewportInfo.width / Math.max(1, fit.width)
          : pagesViewportInfo.height / Math.max(1, fit.height);
      setDiscreteZoom(target);
    };

    window.addEventListener('streamlain:pdf-find-open', onFindOpen);
    window.addEventListener('streamlain:pdf-find-query', onFindQuery);
    window.addEventListener('streamlain:pdf-find-action', onFindAction);
    window.addEventListener('streamlain:pdf-zoom-set', onZoomSet);
    window.addEventListener('streamlain:pdf-zoom-fit', onZoomFit);
    return () => {
      window.removeEventListener('streamlain:pdf-find-open', onFindOpen);
      window.removeEventListener('streamlain:pdf-find-query', onFindQuery);
      window.removeEventListener('streamlain:pdf-find-action', onFindAction);
      window.removeEventListener('streamlain:pdf-zoom-set', onZoomSet);
      window.removeEventListener('streamlain:pdf-zoom-fit', onZoomFit);
    };
  }, [
    closeFindBar,
    goToNextFindHit,
    goToPreviousFindHit,
    meta,
    pagesViewportInfo,
    pdfEventId,
    openFindBar,
    setDiscreteZoom,
  ]);

  const resolvePageHighlightRects = useCallback(
    (
      rects: PDFPageHighlightRect[],
      text: PDFPageTextContent | null,
      pageIndex: number
    ) => {
      const nonSearchRects = rects.filter(rect => rect.kind !== 'search');
      return [
        ...getPageHighlightRects({
          hits: findState.hits,
          activeHit: activeFindHit,
          pageIndex,
          text,
          query: findState.query,
        }),
        ...nonSearchRects,
      ];
    },
    [activeFindHit, findState.hits, findState.query]
  );

  const mainPageContent = useCallback(
    (
      index: number,
      _: unknown,
      {
        viewportInfo,
        meta,
        onPageSelect,
        pageClassName,
        resize,
      }: PDFVirtuosoContext
    ) => {
      return (
        <PDFPageRenderer
          key={`${pageClassName}-${index}`}
          pdf={pdf}
          pageNum={index}
          className={pageClassName}
          viewportInfo={viewportInfo}
          actualSize={meta.pageSizes[index]}
          maxSize={meta.maxSize}
          onSelect={onPageSelect}
          onHighlightClick={onPdfHighlightClick}
          resolveHighlightRects={resolvePageHighlightRects}
          resize={resize}
          highlightRects={[
            ...getPageHighlightRects({
              hits: findState.hits,
              activeHit: activeFindHit,
              pageIndex: index,
              isThumbnail: false,
            }),
            ...getPdfCommentHighlightRects({
              comments,
              storedHighlights: storedHighlights ?? [],
              blockId: source.id,
              sourceId: pdfSourceId,
              pageIndex: index,
              activeCommentId: highlightedCommentId,
            }),
            ...getPdfSelectionHighlightRects({
              selection: pdfSelection,
              pageIndex: index,
            }),
          ]}
          overlay={renderPdfSignatureOverlay(index)}
        />
      );
    },
    [
      activeFindHit,
      comments,
      findState.hits,
      highlightedCommentId,
      onPdfHighlightClick,
      pdfSourceId,
      pdfSelection,
      pdf,
      renderPdfSignatureOverlay,
      resolvePageHighlightRects,
      source.id,
      storedHighlights,
    ]
  );

  const thumbnailPageContent = useCallback(
    (
      index: number,
      _: unknown,
      {
        viewportInfo,
        meta,
        onPageSelect,
        pageClassName,
        resize,
      }: PDFVirtuosoContext
    ) => {
      return (
        <PDFPageRenderer
          key={`${pageClassName}-${index}`}
          pdf={pdf}
          pageNum={index}
          className={clsx(pageClassName, {
            selected: index === cursor,
          })}
          viewportInfo={viewportInfo}
          actualSize={meta.pageSizes[index]}
          maxSize={meta.maxSize}
          onSelect={onPageSelect}
          resize={resize}
          isThumbnail
          highlightRects={[]}
          overlay={renderPdfSignatureOverlay(index, false)}
        />
      );
    },
    [cursor, pdf, renderPdfSignatureOverlay]
  );

  const thumbnailsConfig = useMemo(() => {
    const { height: vh } = viewportInfo;
    const { pageCount, pageSizes, maxSize } = meta;
    const t = Math.min(maxSize.width / maxSize.height, 1);
    const contentWidth = Math.max(72, thumbnailPanelWidth - 26);
    const pw = contentWidth / t;
    const newMaxSize = {
      width: pw,
      height: pw * (maxSize.height / maxSize.width),
    };
    const newPageSizes = pageSizes.map(({ width, height }) => {
      const w = newMaxSize.width * (width / maxSize.width);
      return {
        width: w,
        height: w * (height / width),
      };
    });
    // Full sidebar height minus the 52px thumbnails header — the list runs
    // flush to the bottom screen edge, no reserved strip below it.
    const maxListHeight = Math.max(120, vh - 52);
    const measureViewport = { width: pw, height: maxListHeight };
    // Exact scroll-content height: per-item fitToPage size (same call the
    // thumbnails use) + 2px content-box border per thumbnail + 12px small-gap
    // between items + symmetric 20px ListPadding header/footer. The footer is
    // part of the measured content contract: without it, the selected border
    // of the final thumbnail sits directly on Virtuoso's clipping edge and its
    // bottom stroke disappears through subpixel rounding.
    const contentHeight =
      newPageSizes.reduce(
        (h, size) =>
          h + fitToPage(measureViewport, size, newMaxSize, true).height + 2,
        0
      ) +
      (pageCount - 1) * 12 +
      40;
    const height = Math.min(maxListHeight, contentHeight);
    return {
      context: {
        onPageSelect,
        viewportInfo: {
          width: pw,
          // Thumbnail dimensions must be calculated against the available
          // list height, not the shortened one-page scroll viewport. Feeding
          // `height` back into fitToPage changes its sizing branch and makes
          // the page taller than the viewport that was derived from it.
          height: maxListHeight,
        },
        meta: {
          pageCount,
          maxSize: newMaxSize,
          pageSizes: newPageSizes,
        },
        resize: fitToPage,
        isThumbnail: true,
        pdfId: pdf.id,
        bitmapCacheRole: 'thumbnail',
        pageClassName: styles.pdfThumbnail,
      },
      style: { height },
    };
  }, [meta, pdf.id, viewportInfo, onPageSelect, thumbnailPanelWidth]);

  // Memoized context for the main pages list. A fresh inline object every render
  // (the thumbnail list already memoizes its context) gave every PDFPageRenderer
  // a new `viewportInfo` identity each parent render, re-running its render
  // effect — which calls `page.render.reset()` and cancels the in-flight
  // render. On scroll that thrash starved/cancelled renders and left pages blank
  // (worse the more pages are mounted). Stable identity = renders complete once.
  const pagesContext = useMemo<PDFVirtuosoContext>(
    () => ({
      viewportInfo: pagesViewportInfo,
      meta,
      resize: resizePdfPage,
      pdfId: pdf.id,
      bitmapCacheRole: 'page',
      pageClassName: styles.pdfPage,
      listClassName: styles.pdfPagesList,
      itemClassName: styles.pdfPagesItem,
    }),
    [pagesViewportInfo, meta, pdf.id, resizePdfPage]
  );

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    return observeResize(viewer, ({ contentRect: { width, height } }) => {
      const nextViewport = { width, height };
      if (isThumbnailResizeActiveRef.current) {
        pendingThumbnailResizeViewportRef.current = nextViewport;
        return;
      }
      setViewportInfo(nextViewport);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (fullscreenSaveTimerRef.current !== null) {
        window.clearTimeout(fullscreenSaveTimerRef.current);
      }
      persistFullscreenViewportState();
    };
  }, [persistFullscreenViewportState]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    viewer.addEventListener('wheel', onPdfWheel, { passive: false });
    viewer.addEventListener('gesturestart', onGestureStart);
    viewer.addEventListener('gesturechange', onGestureChange);
    viewer.addEventListener('gestureend', onGestureEnd);

    return () => {
      viewer.removeEventListener('wheel', onPdfWheel);
      viewer.removeEventListener('gesturestart', onGestureStart);
      viewer.removeEventListener('gesturechange', onGestureChange);
      viewer.removeEventListener('gestureend', onGestureEnd);
    };
  }, [onGestureChange, onGestureEnd, onGestureStart, onPdfWheel]);

  // sl-canvas2 (pdf-viewer-resume-search-edgeless): fullscreen restore /
  // navigation controller. The initial PAGE is placed by Virtuoso's
  // initialTopMostItemIndex (seeded from initialFullscreenTarget) so it renders
  // from that index on the first paint — no measure-then-scrollToIndex race.
  // This controller only (1) seeds navigation/persist bookkeeping, (2) restores
  // the intra-page scroll offset once the list is laid out and then opens the
  // persist gate (so a freshly-rendered position can never overwrite the saved
  // state with page 0), and (3) handles a navigation hit that arrives while the
  // viewer is already open.
  const isFullscreenLayoutReady =
    meta.pageCount > 0 && viewportInfo.width > 0 && viewportInfo.height > 0;

  // Seed navigation/persist bookkeeping from the mount-time target once. Done in
  // render (not an effect) so the find-bar effects below already see the correct
  // stale flag and never reopen search for a consumed/bookmarked navigation.
  if (!initialPositionDecidedRef.current) {
    initialPositionDecidedRef.current = true;
    isStaleNavigationRef.current = initialFullscreenTarget.stale;
    appliedNavigationKeyRef.current = initialFullscreenTarget.navigationKey;
    consumedRequestIdRef.current =
      initialFullscreenTarget.consumedRequestId ?? null;
  }

  // Restore the intra-page scroll offset once the list has a real layout, then
  // open the persist gate. The page itself is already placed by Virtuoso.
  useEffect(() => {
    if (initialPositionAppliedRef.current) return;
    if (!isFullscreenLayoutReady) return;
    const target = initialFullscreenTarget;
    isApplyingFullscreenRestoreRef.current = true;
    const outer = window.requestAnimationFrame(() => {
      const inner = window.requestAnimationFrame(() => {
        fullscreenRestoreFrameRef.current = null;
        const el = pagesScrollerRef.current;
        if (el) {
          if (target.scrollLeft > 0) el.scrollLeft = target.scrollLeft;
          if (target.scrollTop > el.scrollTop) el.scrollTop = target.scrollTop;
        }
        isApplyingFullscreenRestoreRef.current = false;
        initialPositionAppliedRef.current = true;
        // Persist the resolved initial position now. For a cold navigation open
        // this records the searched page as the last-opened position; relying
        // on a settling scroll event would miss it when the pages render late.
        persistFullscreenViewportState();
      });
      fullscreenRestoreFrameRef.current = inner;
    });
    fullscreenRestoreFrameRef.current = outer;
    return () => window.cancelAnimationFrame(outer);
  }, [
    isFullscreenLayoutReady,
    initialFullscreenTarget,
    persistFullscreenViewportState,
  ]);

  // A navigation hit may arrive while the viewer is already open (URL params
  // change without remount). The list is measured by then, so scrollToIndex is
  // reliable. The initial navigation is already applied via the mount-time
  // target, so this only fires for a genuinely new navigation key.
  useEffect(() => {
    if (!isFullscreenLayoutReady) return;
    const navigationKey = createPDFViewerNavigationKey(navigation);
    if (!navigationKey || navigationKey === appliedNavigationKeyRef.current) {
      return;
    }
    appliedNavigationKeyRef.current = navigationKey;
    const navPageIndex = normalizePDFPageIndex(
      navigation?.pageIndex,
      meta.pageCount
    );
    if (navPageIndex === null) return;
    const requestId =
      typeof navigation?.requestId === 'string' ||
      typeof navigation?.requestId === 'number'
        ? String(navigation.requestId)
        : null;
    isStaleNavigationRef.current = false;
    consumedRequestIdRef.current = requestId;
    // Persist the navigated page as the last-opened resume position
    // synchronously and unconditionally. The async scroll/rAF path is
    // unreliable here (rAF fires before the cursor ref syncs, and a late PDF
    // render can remount before it runs), so write the known target directly.
    // Intra-page scroll defaults to the page top, matching a fresh nav landing;
    // any later free scroll overwrites it through the normal persist path.
    cursorRef.current = navPageIndex;
    writePdfFullscreenViewportState({
      key: fullscreenStorageKey,
      state: {
        pageIndex: navPageIndex,
        scrollLeft: 0,
        scrollTop: 0,
        updatedAt: Date.now(),
        zoom: zoomRef.current,
        consumedRequestId: requestId ?? undefined,
      },
    });
    const scroller = pagesScrollerHandleRef.current;
    if (!scroller) return;
    isApplyingFullscreenRestoreRef.current = true;
    setCursor(navPageIndex);
    scroller.scrollToIndex({
      index: navPageIndex,
      align: 'center',
      behavior: 'auto',
    });
    thumbnailsScrollerHandleRef.current?.scrollToIndex({
      index: navPageIndex,
      align: 'center',
      behavior: 'auto',
    });
    const frame = window.requestAnimationFrame(() => {
      const inner = window.requestAnimationFrame(() => {
        isApplyingFullscreenRestoreRef.current = false;
        initialPositionAppliedRef.current = true;
        // Persist the navigated page as the last-opened position. The cursor
        // change happened while the restore guard was active (persist skipped),
        // and no further scroll is guaranteed, so write it explicitly here.
        persistFullscreenViewportState();
      });
      fullscreenRestoreFrameRef.current = inner;
    });
    fullscreenRestoreFrameRef.current = frame;
    return () => window.cancelAnimationFrame(frame);
  }, [
    isFullscreenLayoutReady,
    fullscreenStorageKey,
    navigation,
    meta.pageCount,
    persistFullscreenViewportState,
  ]);

  useEffect(() => {
    schedulePersistFullscreenViewportState();
  }, [cursor, schedulePersistFullscreenViewportState, zoom]);

  useEffect(() => {
    if (!navigation?.query) return;
    if (isStaleNavigationRef.current) return;
    dispatchFind({ type: 'open', query: navigation.query });
  }, [navigation?.query]);

  useEffect(() => {
    if (
      navigation?.pageIndex === undefined ||
      !navigation.highlightRects?.length
    ) {
      return;
    }
    if (isStaleNavigationRef.current) return;

    // If the full search results for this exact query are already loaded (e.g.
    // the find bar was open in the PDF before the cmdk hit was clicked), keep
    // that result set and just navigate to the matching hit. Replacing it with
    // a single synthetic hit would collapse the count from "X/48" to "1/1".
    const snapshot = findStateRef.current;
    if (snapshot.query === navigation.query && snapshot.hits.length > 0) {
      const matchIndex = snapshot.hits.findIndex(
        hit => hit.pageIndex === navigation.pageIndex
      );
      if (matchIndex >= 0) {
        dispatchFind({ type: 'activate', index: matchIndex });
      }
      return;
    }

    dispatchFind({
      type: 'results',
      hits: [
        createNavigationPdfSearchHit({
          docId: source.docId,
          attachmentBlockId: source.id,
          pageIndex: navigation.pageIndex,
          query: navigation.query,
          rects: navigation.highlightRects,
        }),
      ],
    });
  }, [
    navigation?.highlightRects,
    navigation?.pageIndex,
    navigation?.query,
    source.docId,
    source.id,
  ]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target instanceof HTMLElement ? event.target : null;
      const editableTarget = target?.closest(
        'input, textarea, select, [contenteditable="true"]'
      );
      if (
        !editableTarget &&
        [
          'ArrowDown',
          'ArrowUp',
          'End',
          'Home',
          'PageDown',
          'PageUp',
          ' ',
        ].includes(event.key)
      ) {
        programmaticPageSelectTargetRef.current = null;
      }
      if (
        !editableTarget &&
        event.key === 'Escape' &&
        armedSignatureAppearanceId
      ) {
        event.preventDefault();
        cancelArmedSignaturePlacement();
        return;
      }
      if (!(event.metaKey || event.ctrlKey)) return;
      if (event.key.toLowerCase() !== 'f') return;
      if (!model) return;
      event.preventDefault();
      dispatchFind({ type: 'open' });
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [armedSignatureAppearanceId, cancelArmedSignaturePlacement, model]);

  useEffect(() => {
    if (!findState.open) return;
    if (!model) return;

    const query = findState.query.trim();
    if (!query) {
      findRequestSeqRef.current += 1;
      return;
    }

    const requestSeq = findRequestSeqRef.current + 1;
    findRequestSeqRef.current = requestSeq;
    dispatchFind({ type: 'loading' });
    const abortController = new AbortController();

    const timeout = window.setTimeout(() => {
      void runHybridPdfFindSource(
        {
          attachmentBlockId: model.id,
          docId: source.docId,
          limit: PDF_FIND_LIMIT,
          localProvider: localPdfTextProvider,
          pageCount: meta.pageCount,
          query,
          server: workspaceServerService.server,
          signal: abortController.signal,
          startPageIndex: cursorRef.current,
          supportsPdfSearchIndex: source.supportsPdfSearchIndex,
          workspaceId: workspaceService.workspace.id,
        },
        update => {
          if (findRequestSeqRef.current !== requestSeq) return;
          dispatchFind({
            type: 'results',
            coverage: update.coverage,
            fallbackActiveHit: getNavigationFallbackFindHit(
              findStateRef.current,
              query
            ),
            hits: update.hits,
            notice: update.notice,
            preserveActive: true,
            sourceMode: update.sourceMode,
          });
        }
      ).catch(() => {
        if (abortController.signal.aborted) return;
        if (findRequestSeqRef.current !== requestSeq) return;
        dispatchFind({ type: 'error', error: 'search-failed' });
      });
    }, 250);

    return () => {
      window.clearTimeout(timeout);
      abortController.abort();
    };
  }, [
    findState.open,
    findState.query,
    localPdfTextProvider,
    meta.pageCount,
    model,
    source.docId,
    source.supportsPdfSearchIndex,
    workspaceServerService.server,
    workspaceService.workspace.id,
  ]);

  useEffect(() => {
    if (findState.scrollNonce === lastFindScrollNonceRef.current) return;
    lastFindScrollNonceRef.current = findState.scrollNonce;
    if (!activeFindHit) return;
    onPageSelect(activeFindHit.pageIndex);
  }, [findState.scrollNonce, activeFindHit, onPageSelect]);

  useEffect(() => {
    if (!source.supportsBlockAnnotations) return;
    const onPdfCommentFocus = (event: Event) => {
      const detail = (event as CustomEvent).detail as
        | {
            commentId?: string;
            blockId?: string;
            pageIndex?: number;
          }
        | undefined;
      if (!detail || detail.blockId !== source.id) return;
      if (typeof detail.pageIndex === 'number') {
        onPageSelect(detail.pageIndex);
      }
      if (detail.commentId) {
        commentEntity?.highlightComment(detail.commentId);
      }
    };

    window.addEventListener('streamlain:pdf-comment-focus', onPdfCommentFocus);
    return () =>
      window.removeEventListener(
        'streamlain:pdf-comment-focus',
        onPdfCommentFocus
      );
  }, [commentEntity, onPageSelect, source.id, source.supportsBlockAnnotations]);

  useEffect(() => {
    if (!commentEntity || !source.supportsBlockAnnotations) return;
    const disposeAdd = commentEntity.onCommentAdded(commentId => {
      const comment = commentEntity.comments$.value.find(
        comment => comment.id === commentId
      );
      if (!comment) return;
      const anchors = comment.targetAnchors.filter(
        (
          anchor
        ): anchor is Extract<CommentTargetAnchor, { type: 'pdf-text' }> =>
          anchor.type === 'pdf-text' && anchor.blockId === source.id
      );
      if (anchors.length === 0) return;
      persistPdfTextHighlight({
        model,
        anchors,
        color: anchors[0].color,
        commentId,
      });
    });
    const disposeDelete = commentEntity.onCommentDeleted(commentId => {
      removePdfTextHighlightForComment({ model, commentId });
    });
    return () => {
      disposeAdd();
      disposeDelete();
    };
  }, [commentEntity, model, source.id, source.supportsBlockAnnotations]);

  const updatePdfSelectionFromPointer = useCallback(
    ({
      current,
      event,
      dragging,
    }: {
      current: PdfGlyphPointer;
      event: ReactPointerEvent<HTMLDivElement>;
      dragging: boolean;
    }) => {
      const root = viewerRef.current;
      const start = pdfSelectionStartRef.current;
      if (!root || !start) return;
      const draft = getPdfSelectionFromGlyphRange({
        root,
        source,
        start,
        end: current,
        toolbar: {
          left: event.clientX,
          top: event.clientY,
        },
        color: DEFAULT_PDF_HIGHLIGHT_COLOR,
      });
      setPdfSelection(draft ? { ...draft, dragging } : null);
    },
    [source]
  );

  const updateArmedSignatureCursorPreview = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!canEditPdfSignatureLayer || !armedSignatureAppearanceId) return;
      const target = event.target instanceof Element ? event.target : null;
      const pageElement =
        target?.closest<HTMLElement>(PDF_PAGE_LAYER_SELECTOR) ?? null;
      const pageBounds = pageElement?.getBoundingClientRect();
      const viewerBounds = viewerRef.current?.getBoundingClientRect();
      setSignatureCursorPreview(
        pageBounds && viewerBounds
          ? {
              left: event.clientX - viewerBounds.left,
              top: event.clientY - viewerBounds.top,
              width: pageBounds.width * PDF_SIGNATURE_DEFAULT_RECT.width,
              height: pageBounds.height * PDF_SIGNATURE_DEFAULT_RECT.height,
            }
          : null
      );
    },
    [armedSignatureAppearanceId, canEditPdfSignatureLayer]
  );

  const placeArmedSignatureAtPointer = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!canEditPdfSignatureLayer || !armedSignatureAppearanceId || !model) {
        return false;
      }
      const target = event.target instanceof Element ? event.target : null;
      const pageElement =
        target?.closest<HTMLElement>(PDF_PAGE_LAYER_SELECTOR) ?? null;
      if (!pageElement) return false;
      const pageIndex = Number(pageElement.dataset.pdfPageIndex);
      if (!Number.isInteger(pageIndex)) return false;
      const bounds = pageElement.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return false;
      const placement = createPdfSignaturePlacement({
        model,
        appearanceId: armedSignatureAppearanceId,
        pageIndex,
        center: {
          x: (event.clientX - bounds.left) / bounds.width,
          y: (event.clientY - bounds.top) / bounds.height,
        },
      });
      if (!placement) return false;
      event.preventDefault();
      event.stopPropagation();
      setSelectedSignatureAppearanceId(armedSignatureAppearanceId);
      setSelectedSignaturePlacementId(placement.id);
      setArmedSignatureAppearanceId('');
      setSignatureCursorPreview(null);
      setSignaturePanelOpen(false);
      notify.success({
        title: t['com.streamlain.pdf.signature.visual.placed'](),
      });
      return true;
    },
    [armedSignatureAppearanceId, canEditPdfSignatureLayer, model, t]
  );

  const onPdfPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      // User interaction takes over the viewport: stop pending zoom-anchor
      // re-asserts and flush an active zoom preview into a real commit.
      programmaticPageSelectTargetRef.current = null;
      zoomScrollAnchorRef.current = null;
      commitContinuousZoom();
      if (event.button !== 0) return;
      if ((event.target as HTMLElement).closest('button')) return;
      if ((event.target as HTMLElement).closest('[role="menu"]')) return;
      if (placeArmedSignatureAtPointer(event)) return;
      const root = viewerRef.current;
      if (!root) return;
      const start = getPdfGlyphAtPoint({
        root,
        clientX: event.clientX,
        clientY: event.clientY,
        allowNearest: false,
      });
      shouldClearPdfSelectionOnClickRef.current = !!pdfSelection;
      if (!start) {
        clearPdfSelection();
        return;
      }
      event.preventDefault();
      event.currentTarget.focus();
      // Only a pointer of a real device can be captured; input the app dispatches for the
      // assistant arrives at what is under it anyway.
      if (event.nativeEvent.isTrusted)
        event.currentTarget.setPointerCapture(event.pointerId);
      window.getSelection()?.removeAllRanges();
      setSelectedPdfHighlight(null);
      pdfSelectionStartRef.current = start;
      updatePdfSelectionFromPointer({
        current: start,
        event,
        dragging: true,
      });
    },
    [
      clearPdfSelection,
      commitContinuousZoom,
      placeArmedSignatureAtPointer,
      pdfSelection,
      updatePdfSelectionFromPointer,
    ]
  );

  const onPdfPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      updateArmedSignatureCursorPreview(event);
      const root = viewerRef.current;
      if (!root || !pdfSelectionStartRef.current) return;
      const current = getPdfGlyphAtPoint({
        root,
        clientX: event.clientX,
        clientY: event.clientY,
        allowNearest: true,
      });
      if (!current) return;
      event.preventDefault();
      updatePdfSelectionFromPointer({
        current,
        event,
        dragging: true,
      });
    },
    [updateArmedSignatureCursorPreview, updatePdfSelectionFromPointer]
  );

  const onPdfPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const root = viewerRef.current;
      const start = pdfSelectionStartRef.current;
      if (!root || !start) return;
      const current =
        getPdfGlyphAtPoint({
          root,
          clientX: event.clientX,
          clientY: event.clientY,
          allowNearest: true,
        }) ?? start;
      event.preventDefault();
      const isClickWithoutRange = comparePdfGlyphPointer(start, current) === 0;
      if (isClickWithoutRange && shouldClearPdfSelectionOnClickRef.current) {
        shouldClearPdfSelectionOnClickRef.current = false;
        clearPdfSelection();
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
        return;
      }
      shouldClearPdfSelectionOnClickRef.current = false;
      suppressNextViewerClickClearRef.current = true;
      updatePdfSelectionFromPointer({
        current,
        event,
        dragging: false,
      });
      pdfSelectionStartRef.current = null;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    },
    [clearPdfSelection, updatePdfSelectionFromPointer]
  );

  const onPdfPointerCancel = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      pdfSelectionStartRef.current = null;
      shouldClearPdfSelectionOnClickRef.current = false;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    },
    []
  );

  const onPdfCopy = useCallback(
    (event: ReactClipboardEvent<HTMLDivElement>) => {
      if (selectedSignaturePlacement) {
        writePdfSignaturePlacementClipboard(
          event.clipboardData,
          selectedSignaturePlacement
        );
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (!pdfSelection?.quote) return;
      event.clipboardData.setData('text/plain', pdfSelection.quote);
      event.preventDefault();
    },
    [pdfSelection, selectedSignaturePlacement]
  );

  const onPdfPaste = useCallback(
    (event: ReactClipboardEvent<HTMLDivElement>) => {
      if (!canEditPdfSignatureLayer) return;
      const copiedPlacement = readPdfSignaturePlacementClipboard(
        event.clipboardData
      );
      if (!copiedPlacement) return;
      if (
        !usableSignatureAppearances.some(
          appearance => appearance.id === copiedPlacement.signatureAppearanceId
        )
      ) {
        return;
      }
      const pageIndex = Math.min(
        Math.max(0, copiedPlacement.pageIndex),
        Math.max(0, meta.pageCount - 1)
      );
      const placement = pastePdfSignaturePlacement({
        model,
        placement: copiedPlacement,
        pageIndex,
      });
      if (!placement) return;
      event.preventDefault();
      event.stopPropagation();
      cancelArmedSignaturePlacement();
      setSignaturePanelOpen(false);
      setSelectedSignatureAppearanceId(placement.signatureAppearanceId ?? '');
      setSelectedSignaturePlacementId(placement.id);
      onPageSelect(pageIndex);
    },
    [
      canEditPdfSignatureLayer,
      cancelArmedSignaturePlacement,
      meta.pageCount,
      model,
      onPageSelect,
      usableSignatureAppearances,
    ]
  );

  const createPdfCommentAnchors = useCallback(() => {
    if (!pdfSelection) return [];
    return pdfSelection.anchors.map(anchor =>
      anchor.type === 'pdf-text'
        ? { ...anchor, color: PDF_COMMENT_HIGHLIGHT_COLOR }
        : anchor
    );
  }, [pdfSelection]);

  const createPdfHighlight = useCallback(
    async (color: string) => {
      if (isSignedPdfCopy || !pdfSelection) return;
      const anchors = pdfSelection.anchors.map(anchor =>
        anchor.type === 'pdf-text' ? { ...anchor, color } : anchor
      );
      persistPdfTextHighlight({
        model,
        anchors: anchors.filter(
          (
            anchor
          ): anchor is Extract<CommentTargetAnchor, { type: 'pdf-text' }> =>
            anchor.type === 'pdf-text'
        ),
        color,
      });
      clearPdfSelection();
    },
    [clearPdfSelection, isSignedPdfCopy, model, pdfSelection]
  );

  const createPdfComment = useCallback(async () => {
    if (isSignedPdfCopy || !commentEntity || !pdfSelection || !model) return;
    const anchors = createPdfCommentAnchors();
    await commentEntity.addComment([], pdfSelection.quote, [model.id], anchors);
    commentPanelService.openCommentPanel();
    clearPdfSelection();
  }, [
    clearPdfSelection,
    commentEntity,
    commentPanelService,
    createPdfCommentAnchors,
    isSignedPdfCopy,
    model,
    pdfSelection,
  ]);

  const isSigningJobRunning =
    signingJob?.status === 'queued' ||
    signingJob?.status === 'running' ||
    signingJob?.status === 'finalizing';
  const canCancelSigningJob =
    signingJob?.status === 'queued' || signingJob?.status === 'running';
  const canStartDigitalSigning = Boolean(
    pdfSourceId &&
    selectedSigningProfile &&
    canEditPdfSignatureLayer &&
    !signatures.isSaving &&
    !isPreparingSigningSnapshot &&
    !isSigningJobRunning
  );
  const hasDigitalSigningMarkups =
    hasRelevantPdfAnnotations || hasRelevantPdfComments;
  const signaturePanelTitle =
    signaturePanelMode === 'digital'
      ? t['com.streamlain.pdf.signature.digital.open']()
      : t['com.streamlain.pdf.signature.panel.title']();

  const viewerColumnsStyle = {
    '--pdf-sidebar-width': `${thumbnailPanelWidth}px`,
    '--pdf-sidebar-layout-width': `${sidebarLayoutWidth}px`,
  } as CSSProperties;

  return (
    <div
      ref={viewerFrameRef}
      data-testid="pdf-viewer"
      className={clsx([
        styles.pdfViewerFrame,
        { gridding: true, scrollable: true },
      ])}
      style={viewerColumnsStyle}
    >
      {hideThumbnails ? null : (
        <aside
          ref={thumbnailSidebarRef}
          className={styles.pdfSidebarColumn}
          data-collapsed={collapsed ? 'true' : undefined}
          data-overlay={thumbnailOverlayMode ? 'true' : undefined}
          aria-label="PDF-Miniaturen"
        >
          {collapsed ? (
            <button
              type="button"
              className={styles.pdfThumbnailsCollapsedToggle}
              onClick={() => setCollapsed(false)}
              aria-label="Miniaturen öffnen"
            >
              <TocIcon aria-hidden style={{ width: 22, height: 22 }} />
            </button>
          ) : (
            <div
              ref={thumbnailPanelRef}
              className={clsx(['thumbnails', styles.pdfThumbnails])}
              style={{ width: thumbnailPanelWidth }}
            >
              {thumbnailOverlayMode ? null : (
                <div
                  className={styles.pdfThumbnailsResizeHandle}
                  onPointerDown={onThumbnailResizePointerDown}
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Miniaturen-Breite ändern"
                />
              )}
              <div className={styles.pdfThumbnailsHeader}>
                <div className={styles.pdfThumbnailsPageCounter}>
                  {meta.pageCount > 0 ? (
                    <PdfThumbnailsPageInput
                      current={cursor + 1}
                      pageCount={meta.pageCount}
                      onJump={pageIndex => onPageSelect(pageIndex, 'auto')}
                    />
                  ) : (
                    <span className="page-cursor">0</span>
                  )}
                  <span
                    className={clsx(
                      'page-count',
                      styles.pdfThumbnailsPageTotal
                    )}
                  >
                    / {meta.pageCount}
                  </span>
                </div>
                <button
                  type="button"
                  className={styles.pdfThumbnailsPanelToggle}
                  onClick={() => setCollapsed(true)}
                  aria-label="Miniaturen einklappen"
                >
                  <span
                    className={styles.pdfThumbnailsCloseGlyph}
                    aria-hidden
                  />
                </button>
              </div>
              <div className={styles.pdfThumbnailsList}>
                <Virtuoso<PDFVirtuosoProps>
                  key={`${pdf.id}-thumbnail`}
                  ref={thumbnailsScrollerHandleRef}
                  className={styles.virtuoso}
                  totalCount={meta.pageCount}
                  itemContent={thumbnailPageContent}
                  components={{
                    Item,
                    List: ListWithSmallGap,
                    Scroller,
                    // Symmetric breathing room keeps the first and last active
                    // thumbnail frames clear of the scroll viewport edges.
                    Header: ListPadding,
                    Footer: ListPadding,
                    ScrollSeekPlaceholder,
                  }}
                  style={thumbnailsConfig.style}
                  context={thumbnailsConfig.context}
                />
              </div>
            </div>
          )}
        </aside>
      )}
      <main
        ref={viewerRef}
        tabIndex={-1}
        className={styles.pdfContentColumn}
        onClick={onViewerBackgroundClick}
        onCopy={onPdfCopy}
        onPaste={onPdfPaste}
        onPointerCancel={onPdfPointerCancel}
        onPointerDown={onPdfPointerDown}
        onPointerLeave={() => setSignatureCursorPreview(null)}
        onPointerMove={onPdfPointerMove}
        onPointerUp={onPdfPointerUp}
      >
        <div className={styles.pdfPagesViewport}>
          <Virtuoso<PDFVirtuosoProps>
            key={pdf.id}
            ref={pagesScrollerHandleRef}
            scrollerRef={updateScrollerRef}
            onScroll={onScroll}
            totalListHeightChanged={onTotalListHeightChanged}
            initialTopMostItemIndex={initialFullscreenTarget.pageIndex}
            increaseViewportBy={prefetchMargins}
            className={clsx(styles.virtuoso, styles.pdfPagesScroller)}
            totalCount={meta.pageCount}
            itemContent={mainPageContent}
            components={{
              Item,
              List,
              Scroller,
              Header: ListPadding,
              Footer: ListEndPadding,
              ScrollSeekPlaceholder,
            }}
            context={pagesContext}
          />
        </div>
        <PdfSearchCoverageBadge
          state={coverage.coverageState}
          hidden={coverage.isHidden}
          disabled={coverage.isDisabled}
          pageReview={{
            locator: indexReviewLocator,
            currentPageIndex: cursor,
            pageCount: meta.pageCount,
            onPageSelect: selectReviewPage,
          }}
          onIndexText={
            coverage.canIndexPdfText
              ? () => {
                  void coverage.indexPdfText();
                }
              : undefined
          }
          onScanImageText={() => {
            void coverage.scanImageText();
          }}
          onCheckAgain={() => {
            coverage.retryCoverageCheck();
          }}
        />
        <PdfSearchCoverageCoachmark
          badgeState={coverage.coverageState}
          dismissed={coverage.coachmarkDismissed}
          hidden={coverage.isHidden}
          onDismissPermanently={coverage.dismissCoachmarkPermanently}
          open={coverage.showCoverageCoachmark}
        />
        {signedPdfVersion ? (
          <PdfSignedBadge version={signedPdfVersion} />
        ) : null}
        {canEditPdfSignatureLayer &&
        armedSignatureAppearance &&
        signatureCursorPreview ? (
          <div
            className={styles.pdfSignatureCursorPreview}
            style={{
              left: signatureCursorPreview.left,
              top: signatureCursorPreview.top,
              width: signatureCursorPreview.width,
              height: signatureCursorPreview.height,
            }}
            aria-hidden="true"
          >
            <img
              alt=""
              src={signatures.appearanceImageUrl(armedSignatureAppearance.id)}
            />
          </div>
        ) : null}
        {signaturePanelOpen && canEditPdfSignatureLayer ? (
          <div
            className={styles.pdfSignaturePanel}
            data-compact={thumbnailOverlayMode ? 'true' : undefined}
            data-mode={signaturePanelMode}
            role="dialog"
            aria-label={signaturePanelTitle}
            onClick={event => event.stopPropagation()}
            onPointerDown={event => event.stopPropagation()}
            onPointerMove={event => event.stopPropagation()}
            onPointerUp={event => event.stopPropagation()}
            onPointerCancel={event => event.stopPropagation()}
            onWheel={event => event.stopPropagation()}
          >
            <div className={styles.pdfSignaturePanelHeader}>
              <span className={styles.pdfSignaturePanelHeaderIcon}>
                <ReauthenticationIcon aria-hidden />
              </span>
              <span className={styles.pdfSignaturePanelHeaderText}>
                <span className={styles.pdfSignaturePanelTitle}>
                  {signaturePanelTitle}
                </span>
                <span className={styles.pdfSignaturePanelSubtitle}>
                  {signaturePanelMode === 'visual'
                    ? t['com.streamlain.pdf.signature.panel.subtitle.visual']()
                    : t[
                        'com.streamlain.pdf.signature.panel.subtitle.digital'
                      ]()}
                </span>
              </span>
              <button
                type="button"
                className={styles.pdfSignaturePanelCloseButton}
                aria-label={t['com.streamlain.pdf.signature.panel.close']()}
                onClick={() => {
                  setSignaturePanelOpen(false);
                  cancelArmedSignaturePlacement();
                }}
              >
                <CloseIcon aria-hidden />
              </button>
            </div>

            {signaturePanelMode === 'visual' ? (
              <>
                <section className={styles.pdfSignaturePanelSection}>
                  {signatureLoadErrorMessage ? (
                    <div className={styles.pdfSignatureEmptyState}>
                      <InformationIcon aria-hidden />
                      <span>{signatureLoadErrorMessage}</span>
                    </div>
                  ) : usableSignatureAppearances.length > 0 ? (
                    <div className={styles.pdfSignatureAppearanceList}>
                      {usableSignatureAppearances.map(appearance => (
                        <button
                          key={appearance.id}
                          type="button"
                          className={styles.pdfSignatureAppearanceCard}
                          data-active={
                            armedSignatureAppearanceId === appearance.id
                              ? 'true'
                              : undefined
                          }
                          aria-label={appearance.ownerDisplayName}
                          title={appearance.ownerDisplayName}
                          onClick={() => armSignatureAppearance(appearance.id)}
                        >
                          <img
                            alt=""
                            src={signatures.appearanceImageUrl(appearance.id)}
                          />
                          <span>{appearance.ownerDisplayName}</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.pdfSignatureEmptyState}>
                      <ImageIcon aria-hidden />
                      <span>
                        {t['com.streamlain.pdf.signature.visual.none']()}
                      </span>
                    </div>
                  )}
                </section>

                <section
                  className={styles.pdfSignaturePanelSection}
                  data-creator="true"
                >
                  <button
                    type="button"
                    className={styles.pdfSignatureCreateButton}
                    aria-expanded={readerSignatureCreatorOpen}
                    onClick={() => {
                      setReaderSignatureCreatorOpen(open => !open);
                    }}
                  >
                    <span>
                      {t['com.streamlain.pdf.signature.upload.create']()}
                    </span>
                  </button>
                  {readerSignatureCreatorOpen ? (
                    <PdfSignatureCreator
                      workspaceId={workspaceService.workspace.id}
                      isSaving={signatures.isSaving}
                      onCreate={createReaderSignatureAppearance}
                      resolveClipboardBlob={resolveReaderSignatureClipboardBlob}
                    />
                  ) : null}
                </section>
              </>
            ) : null}

            {signaturePanelMode === 'digital' ? (
              <PdfDigitalSignaturePanel
                step={digitalSigningStep}
                loadErrorMessage={signatureLoadErrorMessage}
                isBusy={signatures.isSaving}
                selectedProfileId={selectedSigningProfile?.id ?? ''}
                onSelectProfile={setSelectedSigningProfileId}
                hasMarkups={hasDigitalSigningMarkups}
                includeMarkups={includeAnnotationsInDigitalSignature}
                onIncludeMarkupsChange={setIncludeAnnotationsInDigitalSignature}
                job={signingJob}
                isPreparing={isPreparingSigningSnapshot}
                canStart={canStartDigitalSigning}
                onStart={() => {
                  void startDigitalSigning();
                }}
                onCancelJob={
                  canCancelSigningJob && signingJob
                    ? () => {
                        void signatures
                          .cancelSigningJob(signingJob.id)
                          .then(setSigningJob)
                          .catch(error => {
                            notifyPdfSignatureError(error, 'cancel');
                          });
                      }
                    : null
                }
                signedVersions={signatures.signedVersions}
                newestVersionId={completedSigningVersion?.id ?? null}
                canPlace={canPlaceSignedCopies}
                placingVersionId={placingSignedVersionId}
                onPlace={version => {
                  void placeSignedCopyOnCanvas(version);
                }}
                downloadUrl={signatures.signedVersionDownloadUrl}
                onActivateProfile={profile => {
                  void signatures
                    .activateSigningProfile(profile)
                    .catch(error => {
                      notifyPdfSignatureError(error, 'save');
                    });
                }}
                onOpenPersonalSettings={openSigningProfileSettings}
                onOpenWorkspaceSettings={
                  canManageSignatureServices
                    ? openSignatureServiceSettings
                    : null
                }
                onOpenPlans={canOpenWorkspacePlans ? openWorkspacePlans : null}
              />
            ) : null}
          </div>
        ) : null}
        {pdfSelection && !pdfSelection.dragging && !isSignedPdfCopy ? (
          <div
            className={styles.pdfAnnotationToolbar}
            role="toolbar"
            aria-label={t['com.streamlain.pdf.annotation.toolbar']()}
            style={{
              left: pdfSelection.toolbar.left,
              top: pdfSelection.toolbar.top,
            }}
            onMouseDown={event => event.preventDefault()}
          >
            <Menu
              items={
                <div className={styles.pdfAnnotationHighlightMenu}>
                  {PDF_HIGHLIGHT_COLORS.map(color => (
                    <button
                      key={color.id}
                      type="button"
                      className={styles.pdfAnnotationHighlightMenuItem}
                      aria-label={formatPdfHighlightColorLabel(color.id)}
                      onClick={() => {
                        void createPdfHighlight(color.value);
                      }}
                    >
                      <span
                        className={styles.pdfAnnotationHighlightMenuSwatch}
                        style={{ background: color.value }}
                      >
                        A
                      </span>
                      <span>{formatPdfHighlightColorLabel(color.id)}</span>
                    </button>
                  ))}
                </div>
              }
              contentOptions={{
                sideOffset: 8,
                className: styles.pdfAnnotationHighlightMenuContent,
              }}
            >
              <button
                type="button"
                className={styles.pdfAnnotationToolbarButton}
                data-combo="true"
                aria-label={t['com.streamlain.pdf.annotation.highlight']()}
              >
                <HighLightLinearIcon />
                <ArrowDownSmallIcon style={{ width: 14, height: 14 }} />
              </button>
            </Menu>
            <button
              type="button"
              className={styles.pdfAnnotationToolbarButton}
              onClick={() => {
                void createPdfComment();
              }}
              aria-label={t['com.streamlain.pdf.annotation.comment']()}
            >
              <CommentIcon />
            </button>
          </div>
        ) : null}
        {selectedPdfHighlight && !isSignedPdfCopy ? (
          <div
            className={styles.pdfAnnotationToolbar}
            role="toolbar"
            aria-label={t['com.streamlain.pdf.annotation.highlight']()}
            style={{
              left: selectedPdfHighlight.toolbar.left,
              top: selectedPdfHighlight.toolbar.top,
            }}
            onMouseDown={event => event.preventDefault()}
            onClick={event => event.stopPropagation()}
          >
            <button
              type="button"
              className={styles.pdfAnnotationToolbarButton}
              data-danger="true"
              onClick={deleteSelectedPdfHighlight}
              aria-label={t['Delete']()}
            >
              <DeleteIcon />
            </button>
          </div>
        ) : null}
      </main>
      {commentEntity ? (
        <aside
          className={styles.pdfCommentsColumn}
          data-open={commentsOpen ? 'true' : undefined}
          data-overlay={thumbnailOverlayMode ? 'true' : undefined}
          data-testid="pdf-viewer-comments"
          aria-label={t['com.streamlain.rightSidebar.comments']()}
          aria-hidden={commentsOpen ? undefined : true}
          onKeyDown={event => {
            // Escape ends an edit or a highlight here; it does not close the
            // reader over a comment that is being written.
            if (event.key !== 'Escape') return;
            event.stopPropagation();
            commentEntity.highlightComment(null);
            commentEntity.dismissDraftEditing();
          }}
        >
          {commentsOpen ? (
            <div className={styles.pdfCommentsPanel}>
              <div className={styles.pdfCommentsHeader}>
                <span className={styles.pdfCommentsTitle}>
                  {t['com.streamlain.rightSidebar.comments']()}
                </span>
                <button
                  type="button"
                  className={styles.pdfThumbnailsPanelToggle}
                  onClick={closeComments}
                  aria-label={t['com.streamlain.pdf.viewer.comments.close']()}
                  title={t['com.streamlain.pdf.viewer.comments.close']()}
                  data-testid="pdf-viewer-comments-close"
                >
                  <CloseIcon aria-hidden />
                </button>
              </div>
              <div className={styles.pdfCommentsList}>
                <CommentPanel entity={commentEntity} />
              </div>
            </div>
          ) : null}
        </aside>
      ) : null}
    </div>
  );
};

type PDFViewerStatusProps = {
  message: string;
  reload: () => void;
};

function PDFViewerStatusMenuItems({ message, reload }: PDFViewerStatusProps) {
  const onClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();

      reload();
    },
    [reload]
  );

  return (
    <div className={styles.pdfStatusMenu}>
      <div>{message}</div>
      <div className={styles.pdfStatusMenuFooter}>
        <button
          data-testid="pdf-viewer-reload"
          className={styles.pdfReloadButton}
          onClick={onClick}
        >
          Reload
        </button>
      </div>
    </div>
  );
}

function PDFViewerStatus(props: PDFViewerStatusProps) {
  return (
    <div className={styles.pdfStatus} data-testid="pdf-viewer-status-wrapper">
      <Menu
        items={<PDFViewerStatusMenuItems {...props} />}
        contentWrapperStyle={{
          padding: '8px',
          boxShadow: cssVarV2('shadow/overlay'),
        }}
        contentOptions={{
          sideOffset: 8,
        }}
      >
        <button
          data-testid="pdf-viewer-status"
          className={styles.pdfStatusButton}
        >
          <InformationIcon />
        </button>
      </Menu>
    </div>
  );
}

function PDFViewerContainer({
  model,
  source,
  reload,
  ...props
}: AttachmentViewerBaseProps & { reload: () => void }) {
  const pdfService = useService(PDFService);
  const [pdf, setPdf] = useState<PDF | null>(null);
  const state = useLiveData(
    useMemo(
      () =>
        pdf?.state$ ??
        new LiveData<PDFRendererState>({ status: PDFStatus.IDLE }),
      [pdf]
    )
  );
  const blobId = useLiveData(
    useMemo(
      () =>
        model
          ? LiveData.fromSignal(model.props.sourceId$)
          : new LiveData(source.blobId),
      [model, source.blobId]
    )
  );

  useEffect(() => {
    if (state.status !== PDFStatus.Error) return;

    track.$.attachment.$.openPDFRendererFail();
  }, [state]);

  useEffect(() => {
    if (!blobId) return;

    const { pdf, release } = pdfService.get(blobId);
    setPdf(pdf);

    return () => {
      release();
    };
  }, [blobId, pdfService, setPdf]);

  if (pdf && state.status === PDFStatus.Opened) {
    return (
      <PDFViewerInner
        {...props}
        source={source}
        model={model}
        pdf={pdf}
        meta={state.meta}
      />
    );
  }

  return (
    <>
      <PDFLoading />
      {state.status === PDFStatus.Error && (
        <PDFViewerStatus message={state.error.message} reload={reload} />
      )}
    </>
  );
}

const PDFLoading = () => (
  <div className={styles.pdfLoadingWrapper}>
    <LoadingSvg />
  </div>
);

export function PDFViewer(props: AttachmentViewerBaseProps) {
  const [refreshKey, setRefreshKey] = useState<string | null>(null);
  const reload = useCallback(() => setRefreshKey(nanoid()), []);
  return <PDFViewerContainer key={refreshKey} reload={reload} {...props} />;
}
